import { apiRequest, apiUpload } from '../api';
import { state } from '../state';
import { t, getLocale, onLocaleChange } from '../i18n';
import {
    listRefs,
    createRef,
    type GameFormat,
    type GameHost,
    type GameMap,
    type GameMod,
} from '../api/game-refs';
import { openAddMapModal, openEditMapModal, type MapPrefill } from '../ui/map-modal';
import { openGameDetailModal } from '../ui/player-detail';
import { buildGameOverlayButton } from '../ui/overlay-output';
import { buildReactionsBlock, resetReactionsCache } from '../ui/reactions';
import { shareGameCard } from '../share';
import {
    findReplayMap,
    matchReplayPlayer,
    normalizeReplayRace,
    normalizeNick,
    stripClanTag,
    formatLobbySettings,
    type LobbySetting,
    type ReplayArcadeMap,
    type ReplayPrefill,
    type ReplayPrefillPlayer,
} from '../ui/replay-import';
import {
    cacheReplay,
    forgetReplayDirectory,
    getCachedReplay,
    getRememberedReplayDirectory,
    isDirectoryPickerSupported,
    listReplayFiles,
    pickReplayDirectory,
    pruneReplayCache,
    queryReplayDirectoryPermission,
    rememberReplayDirectory,
    requestReplayDirectoryPermission,
    type ReplayDirHandle,
} from '../ui/replay-folder';
import type { PlayerWithStats, PlayersListResponse } from '../types';
import type { GameListItem, GamesListResponse, GameFull } from '../types-games';
import { ensureRolesLoaded } from '../app';
import terranIconUrl from '../../assets/race/terran.svg';
import zergIconUrl from '../../assets/race/zerg.svg';
import protossIconUrl from '../../assets/race/protoss.svg';
import randomIconUrl from '../../assets/race/random.svg';

// ============================================================
// Состояние модуля
// ============================================================
let abortController: AbortController | null = null;
let cachedGames: GameListItem[] = [];
let searchQuery = '';

/** Размер страницы подгрузки игр (совпадает с дефолтом сервера). */
const GAMES_PAGE_SIZE = 50;
/** Есть ли ещё более ранние игры для подгрузки. */
let hasMoreGames = false;
/** Идёт ли сейчас подгрузка следующей страницы (защита от параллельных вызовов). */
let isLoadingMore = false;
/** Наблюдатель за нижней границей списка — триггерит подгрузку. */
let loadMoreObserver: IntersectionObserver | null = null;
/** Пользователь уже прокручивал список — только тогда включаем автоподгрузку
 *  (иначе из-за свёрнутых дней сентинел виден сразу и подгрузка сработала бы
 *  мгновенно, без желания пользователя). */
let userHasScrolled = false;

/** Отметка последнего открытия раздела «Игры» — для метки «новое». */
const GAMES_SEEN_KEY = 'games.lastSeen';
/** Время прошлого открытия раздела (null — первый визит, метки не ставим). */
let gamesSeenAt: number | null = null;
/** Игры, появившиеся после прошлого открытия раздела. */
let newGameIds = new Set<number>();
/** Плашку закрыли вручную — до конца визита не показываем снова. */
let newBannerDismissed = false;

let cachedPlayers: PlayerWithStats[] = [];
let cachedFormats: GameFormat[] = [];
let cachedHosts: GameHost[] = [];
let cachedMaps: GameMap[] = [];
let cachedMods: GameMod[] = [];

const filters = {
    formatId: null as number | null,
    hostId: null as number | null,
    mapId: null as number | null,
    /** Фильтр «карта удалена»: игры, чья карта мягко удалена. */
    mapDeleted: false,
    modName: null as string | null,
    playerId: null as number | null,
};

let playerFilterName: string | null = null;

// ============================================================
// Форма
// ============================================================
interface GamePlayerDraft {
    player_id: number | null;
    race: 'T' | 'Z' | 'P' | 'R';
    team: number | null;
    is_winner: boolean;
    /** Итоговое место: 1 — победитель, 2..N — остальные. null — не выбрано. */
    place: number | null;
    /** Имя из реплея — показывается в автокомплите, пока игрок не сопоставлен. */
    raw_name?: string | null;
    /** Черновик пришёл из реплея (для валидации перед сохранением). */
    from_replay?: boolean;
    /** Общий счёт из реплея (добыто + уничтожено + потеряно + живо) — для сортировки и мест. */
    replay_score?: number | null;
    /** Составляющие счёта — для подсказки. */
    replay_gathered?: number | null;
    replay_killed?: number | null;
    replay_lost?: number | null;
    replay_army?: number | null;
    /** Сколько секунд игрок продержался в игре (из eco-таймлайна). */
    replay_survival?: number | null;
}

// ============================================================
// Черновик игры от «Менеджера матча»
// ============================================================
/**
 * Черновик игры от «Менеджера матча» (`/tools/match`): кладётся в
 * sessionStorage и открывается формой новой игры по `/games?create=match`.
 */
export interface MatchGamePrefill {
    /** Дата/время в формате input[type=datetime-local] (или null — текущее время). */
    played_at: string | null;
    map_id: number | null;
    mod_id: number | null;
    players: Array<{ player_id: number; race: 'T' | 'Z' | 'P' | 'R' }>;
    /** Опции лобби мода из реплея, импортированного в «Менеджере матча». */
    lobby_settings?: LobbySetting[];
}

/** Ключ sessionStorage с черновиком от «Менеджера матча». */
export const MATCH_PREFILL_KEY = 'games.matchPrefill';

/** Порог «близкого счёта»: если соседние места ближе 3%, порядок может быть неверным. */
const CLOSE_SCORE_GAP = 0.03;

let drafts: GamePlayerDraft[] = [];
let editingGameId: number | null = null;
let isDirty = false;

let modal: HTMLElement | null = null;
let form: HTMLFormElement | null = null;
let modalTitle: HTMLElement | null = null;
let playersListBox: HTMLElement | null = null;
let formatSelect: HTMLSelectElement | null = null;
let hostSelect: HTMLSelectElement | null = null;
let mapSelect: HTMLSelectElement | null = null;
let mapSearchInput: HTMLInputElement | null = null;
let mapDropdown: HTMLElement | null = null;
let mapHighlight = -1;
let modSelect: HTMLSelectElement | null = null;
let playedAtInput: HTMLInputElement | null = null;
let durationInput: HTMLInputElement | null = null;
let notesInput: HTMLTextAreaElement | null = null;
let isTeamCheckbox: HTMLInputElement | null = null;
let teamSizeSelect: HTMLSelectElement | null = null;
let teamCountSelect: HTMLSelectElement | null = null;
let teamModeOptions: HTMLElement | null = null;
let placementHintEl: HTMLElement | null = null;
/** Плашка «реплей полный / неполный» в форме игры. */
let replayCompletenessEl: HTMLElement | null = null;
/** Предупреждение «реплей уже был добавлен» + ссылка на игру. */
let replayDuplicateEl: HTMLElement | null = null;
/** Подсказка «кто из наблюдателей может быть ведущим». */
let replayHostHintEl: HTMLElement | null = null;

// --- Импорт игр из папки с реплеями ---
/** Сколько последних реплеев разбираем из выбранной папки. */
const REPLAY_FOLDER_LIMIT = 10;
interface ReplayFolderItem {
    file: File;
    prefill: ReplayPrefill | null;
    error: string | null;
    cached?: boolean;
}
let replayFolderPanelEl: HTMLElement | null = null;
let replayFolderItems: ReplayFolderItem[] = [];
let replayFolderNotice: string | null = null;
let replayFolderParsing = false;
let replayFolderTotal = 0;
let replayFolderDone = 0;
/** Папка, из которой сейчас показаны реплеи (File System Access API). */
let replayFolderHandle: ReplayDirHandle | null = null;
/** Сохранённая папка, к которой ещё нужно запросить доступ по клику. */
let replayFolderRemembered: ReplayDirHandle | null = null;
/** Все найденные реплеи (новые сверху) и сколько уже показано. */
let replayFolderFiles: File[] = [];
let replayFolderLoaded = 0;
/** Уже добавленные игры по id реплея: id реплея → игра (для пометки дубликатов). */
const replayGameHits = new Map<string, { game_id: number; played_at: string | null }>();
/** Id реплеев, которые уже проверяли на дубликаты (чтобы не спрашивать повторно). */
const replayCheckedIds = new Set<string>();
/** Id реплея, из которого открыта текущая форма новой игры (null — не из реплея). */
let currentReplayId: string | null = null;
/** Id настроек лобби из этого реплея: при сохранении сервер привяжет их к выбранному моду. */
let currentReplayModNamespaces: number[] = [];
/** Опции лобби мода из этого реплея: сохраняются в игру. */
let currentReplayLobbySettings: LobbySetting[] = [];
let replayLobbySettingsEl: HTMLElement | null = null;
/** Карта из реплея, которой нет на сайте: из неё заполняется форма новой карты. */
let currentReplayArcadeMap: ReplayArcadeMap | null = null;

const LAST_GAME_KEY = 'games:lastGameSettings';

interface LastGameSettings {
    format_id: number | null;
    host_id: number | null;
    map_id: number | null;
    mod_id: number | null;
    duration_min: number | null;
    is_team: boolean;
    team_size: number;
    team_count: number;
    track_elim: boolean;
    played_at: string | null;   // ← НОВОЕ: время последней игры
    players: Array<{
        player_id: number | null;
        race: 'T' | 'Z' | 'P' | 'R';
        team: number | null;
    }>;
}

const TEAM_COLORS = [
    '#3498db',
    '#e74c3c',
    '#2ecc71',
    '#f1c40f',
    '#9b59b6',
    '#e67e22',
];

// ============================================================
// Mount / unmount
// ============================================================
export function mountGames(params: URLSearchParams): void {
    const screen = document.getElementById('screen-games');
    if (screen) screen.classList.remove('hidden');

    abortController = new AbortController();
    const { signal } = abortController;

    // Сводки реакций могли устареть, пока страница была закрыта.
    resetReactionsCache();

    // Метка «новое»: запоминаем прошлый визит и сразу обновляем отметку.
    gamesSeenAt = readGamesSeenAt();
    newGameIds = new Set();
    newBannerDismissed = false;
    markGamesSectionSeen();

    modal = document.getElementById('game-modal');
    form = document.getElementById('game-form') as HTMLFormElement | null;
    modalTitle = document.getElementById('game-modal-title');
    playersListBox = document.getElementById('players-list');
    formatSelect = document.getElementById('field-format') as HTMLSelectElement | null;
    hostSelect = document.getElementById('field-host') as HTMLSelectElement | null;
    mapSelect = document.getElementById('field-map') as HTMLSelectElement | null;
    mapSearchInput = document.getElementById('field-map-search') as HTMLInputElement | null;
    mapDropdown = document.getElementById('map-combobox-dropdown');
    modSelect = document.getElementById('field-mod') as HTMLSelectElement | null;
    playedAtInput = document.getElementById('field-played-at') as HTMLInputElement | null;
    durationInput = document.getElementById('field-duration') as HTMLInputElement | null;
    replayCompletenessEl = document.getElementById('replay-completeness');
    replayDuplicateEl = document.getElementById('replay-duplicate');
    replayHostHintEl = document.getElementById('replay-host-hint');
    replayLobbySettingsEl = document.getElementById('replay-lobby-settings');
    replayFolderPanelEl = document.getElementById('games-replays-panel');
    notesInput = document.getElementById('field-notes') as HTMLTextAreaElement | null;
    isTeamCheckbox = document.getElementById('field-is-team') as HTMLInputElement | null;
    teamSizeSelect = document.getElementById('field-team-size') as HTMLSelectElement | null;
    teamCountSelect = document.getElementById('field-team-count') as HTMLSelectElement | null;
    teamModeOptions = document.getElementById('team-mode-options');
    placementHintEl = document.getElementById('players-placement-hint');

    filters.formatId = params.get('format') ? Number(params.get('format')) : null;
    filters.hostId = params.get('host') ? Number(params.get('host')) : null;
    const mapParam = params.get('map');
    filters.mapDeleted = mapParam === 'deleted';
    filters.mapId = mapParam && mapParam !== 'deleted' && Number.isInteger(Number(mapParam))
        ? Number(mapParam)
        : null;
    filters.modName = params.get('mod') || null;

    const playerParam = params.get('player_id');
    filters.playerId = playerParam && Number.isInteger(Number(playerParam))
        ? Number(playerParam)
        : null;
    playerFilterName = null;
    if (filters.playerId !== null) void loadPlayerFilterName(filters.playerId);

    // Проверяем роли, потом настраиваем кнопки
    void ensureRolesLoaded().then(() => {
        updateCreateButtonVisibility();
    });
    updateCreateButtonVisibility(); // первичный вызов (если роли уже есть)

    const urlQ = params.get('q');
    if (urlQ) {
        searchQuery = urlQ.toLowerCase();
        const searchInput = document.getElementById('games-search') as HTMLInputElement | null;
        if (searchInput) searchInput.value = urlQ;
    }

    const createBtn = document.getElementById('btn-create-game');
    if (createBtn && (state.user?.is_moderator || state.user?.is_admin)) {
        createBtn.removeAttribute('hidden');
        createBtn.addEventListener('click', () => {
            void openCreateGameModal();
        }, { signal });
    } else if (createBtn) {
        createBtn.setAttribute('hidden', '');
    }

    const replayBtn = document.getElementById('btn-add-from-replay');
    const replayInput = document.getElementById('replay-file-input') as HTMLInputElement | null;
    replayBtn?.addEventListener('click', () => replayInput?.click(), { signal });
    replayInput?.addEventListener('change', () => {
        const file = replayInput.files?.[0];
        if (file) void openReplayGameModal(file);
        replayInput.value = '';
    }, { signal });

    // «Папка реплеев»: разбираем последние 10 реплеев и показываем списком.
    replayFolderItems = [];
    replayFolderNotice = null;
    replayFolderParsing = false;
    replayFolderTotal = 0;
    replayFolderDone = 0;
    replayFolderHandle = null;
    replayFolderRemembered = null;
    replayFolderFiles = [];
    replayFolderLoaded = 0;
    void pruneReplayCache();
    const folderBtn = document.getElementById('btn-games-replays') as HTMLButtonElement | null;
    const folderInput = document.getElementById('games-replay-folder') as HTMLInputElement | null;
    folderBtn?.addEventListener('click', () => {
        void chooseReplayFolder();
    }, { signal });
    folderInput?.addEventListener('change', () => {
        const files = replayFileList(folderInput.files ? [...folderInput.files] : []);
        folderInput.value = '';
        void openReplayFolder(files);
    }, { signal });
    renderReplayFolderPanel();
    void restoreReplayFolder();

    // Быстрый импорт: файл реплея можно бросить в любое место раздела —
    // так не нужно целиться в маленькую кнопку «Из реплея».
    const dropZone = document.getElementById('games-replay-dropzone');
    if (dropZone) {
        const hasFiles = (e: DragEvent): boolean =>
            Array.from(e.dataTransfer?.types ?? []).includes('Files');
        // Импорт из реплея доступен только модераторам/админам (как и кнопка).
        const canImport = (): boolean => Boolean(state.user?.is_moderator || state.user?.is_admin);
        // Форма уже открыта — не затираем введённые данные новым импортом.
        const isFormOpen = (): boolean => Boolean(modal && !modal.classList.contains('hidden'));
        // Пока идёт разбор предыдущего файла, второй бросок игнорируем.
        const canHandle = (e: DragEvent): boolean =>
            hasFiles(e)
            && canImport()
            && !isFormOpen()
            && !dropZone.classList.contains('is-parsing');
        const setZone = (on: boolean, parsing = false): void => {
            dropZone.classList.toggle('hidden', !on);
            dropZone.classList.toggle('is-parsing', parsing);
        };

        // dragenter/dragleave приходят на каждый дочерний элемент — считаем глубину,
        // иначе зона мигает при движении курсора по странице.
        let dragDepth = 0;

        document.addEventListener('dragenter', (e) => {
            if (!canHandle(e)) return;
            e.preventDefault();
            dragDepth += 1;
            setZone(true);
        }, { signal });

        document.addEventListener('dragover', (e) => {
            if (!canHandle(e)) return;
            // Без preventDefault браузер уйдёт по адресу брошенного файла.
            e.preventDefault();
            if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
        }, { signal });

        document.addEventListener('dragleave', (e) => {
            if (!hasFiles(e)) return;
            dragDepth = Math.max(0, dragDepth - 1);
            if (dragDepth === 0) setZone(false);
        }, { signal });

        document.addEventListener('drop', (e) => {
            if (!canHandle(e)) return;
            dragDepth = 0;
            setZone(false);
            e.preventDefault();
            const file = e.dataTransfer?.files?.[0];
            if (file) openReplayFromFile(file);
        }, { signal });

        // Страховка: если файл увели за пределы окна, dragleave может не прийти.
        window.addEventListener('blur', () => {
            dragDepth = 0;
            setZone(false);
        }, { signal });
    }

    const searchInput = document.getElementById('games-search') as HTMLInputElement | null;
    searchInput?.addEventListener('input', () => {
        searchQuery = searchInput.value.trim().toLowerCase();
        renderGames();
    }, { signal });

    document.getElementById('filter-format')?.addEventListener('change', (e) => {
        const v = (e.target as HTMLSelectElement).value;
        filters.formatId = v ? Number(v) : null;
        applyFilters();
    }, { signal });

    document.getElementById('filter-host')?.addEventListener('change', (e) => {
        const v = (e.target as HTMLSelectElement).value;
        filters.hostId = v ? Number(v) : null;
        applyFilters();
    }, { signal });

    document.getElementById('filter-map')?.addEventListener('change', (e) => {
        const v = (e.target as HTMLSelectElement).value;
        if (v === 'deleted') {
            filters.mapDeleted = true;
            filters.mapId = null;
        } else {
            filters.mapDeleted = false;
            filters.mapId = v ? Number(v) : null;
        }
        applyFilters();
    }, { signal });

    document.getElementById('btn-reset-filters')?.addEventListener('click', () => {
        resetFilters();
    }, { signal });

    document.getElementById('game-modal-close')?.addEventListener('click', () => closeGameModal(), { signal });
    document.getElementById('btn-cancel-game')?.addEventListener('click', () => closeGameModal(), { signal });
    form?.addEventListener('submit', (e) => void submitGameForm(e), { signal });

    // Закрываем открытые выпадающие списки рас при клике вне их
    document.addEventListener('click', () => {
        document.querySelectorAll('.race-picker-menu').forEach((m) => m.remove());
    }, { signal });

    document.getElementById('btn-add-player')?.addEventListener('click', () => {
        addPlayerDraft();
    }, { signal });

    document.getElementById('btn-auto-places')?.addEventListener('click', () => {
        autoAssignPlaces();
    }, { signal });

    document.getElementById('btn-create-player-inline')?.addEventListener('click', () => {
        void createPlayerInline();
    }, { signal });

    // «+» — добавить карту через общую модалку, «✎» — править выбранную.
    document.getElementById('btn-add-map')?.addEventListener('click', () => {
        void addMapFromModal();
    }, { signal });

    document.getElementById('btn-edit-map')?.addEventListener('click', () => {
        void editSelectedMapFromModal();
    }, { signal });

    // Комбобокс карты: ввод = поиск, выбор из списка задаёт карту.
    mapSearchInput?.addEventListener('input', () => {
        showMapDropdown(mapSearchInput!.value);
    }, { signal });

    mapSearchInput?.addEventListener('focus', () => {
        // При фокусе показываем весь список, а текст выбранной карты выделяем,
        // чтобы ввод сразу заменял его поисковой строкой.
        showMapDropdown('');
        mapSearchInput?.select();
    }, { signal });

    mapSearchInput?.addEventListener('blur', () => {
        // Даём отработать mousedown по элементу списка, затем возвращаем текст.
        setTimeout(() => {
            hideMapDropdown();
            syncMapInput();
        }, 150);
    }, { signal });

    mapSearchInput?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault(); // Enter не должен отправлять форму игры
            const hl = mapDropdown?.querySelector<HTMLElement>('.player-autocomplete-item.is-highlighted');
            const items = mapDropdown?.querySelectorAll<HTMLElement>('.player-autocomplete-item');
            const target = hl ?? (items && items.length === 1 ? items[0] : null);
            const id = target?.dataset.mapId;
            if (id) selectMapById(Number(id));
            return;
        }
        if (e.key === 'Escape') {
            if (mapDropdown && !mapDropdown.classList.contains('hidden')) {
                e.stopPropagation();
                hideMapDropdown();
            }
            return;
        }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            if (!mapDropdown || mapDropdown.classList.contains('hidden')) return;
            e.preventDefault();
            moveMapHighlight(e.key === 'ArrowDown' ? 1 : -1);
        }
    }, { signal });

    document.getElementById('btn-add-mod')?.addEventListener('click', () => {
        void createModInline();
    }, { signal });

    document.getElementById('btn-clear-form')?.addEventListener('click', () => {
        if (!confirm(t('games.clear_form_confirm'))) return;
        clearLastGameSettings();
        void openCreateGameModal();
    }, { signal });

    document.getElementById('btn-add-5min')?.addEventListener('click', () => {
        if (!playedAtInput?.value) {
            playedAtInput!.value = toDatetimeLocal(new Date());
        }
        if (!playedAtInput) {
            alert("playedAtInput is null");
            return;
        }

        const current = new Date(playedAtInput!.value);
        current.setMinutes(current.getMinutes() + 5);
        playedAtInput!.value = toDatetimeLocal(current);
        markDirty();
    }, { signal });

    document.getElementById('btn-now')?.addEventListener('click', () => {
        if (!playedAtInput) return;
        playedAtInput.value = toDatetimeLocal(new Date());
        markDirty();
    }, { signal });

    isTeamCheckbox?.addEventListener('change', () => {
        updateTeamModeVisibility();
        if (isTeamMode()) enableTeamMode();
        else for (const d of drafts) d.team = null;
        renderPlayerDrafts();
        markDirty();
    }, { signal });

    teamSizeSelect?.addEventListener('change', () => {
        ensureTeamSlots();
        renderPlayerDrafts();
        markDirty();
    }, { signal });

    teamCountSelect?.addEventListener('change', () => {
        ensureTeamSlots();
        renderPlayerDrafts();
        markDirty();
    }, { signal });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal && !modal.classList.contains('hidden')) {
            closeGameModal();
        }
    }, { signal });

    // Подписку снимаем при unmount (abort), иначе она копится с каждым заходом.
    const offLocale = onLocaleChange(() => {
        renderGames();
        updateTeamModeVisibility();
        if (modal && !modal.classList.contains('hidden')) {
            renderPlayerDrafts();
        }
    });
    signal.addEventListener('abort', offLocale, { once: true });

    // Подгрузка более ранних игр: кнопка + инфинити-скролл по сентинелу.
    userHasScrolled = false;
    window.addEventListener('scroll', () => { userHasScrolled = true; },
        { signal, passive: true, once: true });
    const loadMoreBox = document.getElementById('games-load-more');
    document.getElementById('btn-load-more')?.addEventListener('click', () => {
        void loadMoreGames();
    }, { signal });
    if (loadMoreBox && 'IntersectionObserver' in window) {
        loadMoreObserver = new IntersectionObserver((entries) => {
            if (userHasScrolled && entries.some((e) => e.isIntersecting)) void loadMoreGames();
        }, { rootMargin: '400px' });
        loadMoreObserver.observe(loadMoreBox);
    }

    // Сначала справочники (карты нужны для подсветки удалённых), затем игры.
    void (async () => {
        await setupFilterSelects();
        await loadGames();
        const highlightId = params.get('highlight');
        if (highlightId) await highlightGameTillFound(Number(highlightId));
        // Прямая ссылка на игру (например, из предупреждения о дубликате реплея).
        const gameParam = Number(params.get('game'));
        if (Number.isInteger(gameParam) && gameParam > 0) {
            const url = new URL(window.location.href);
            url.searchParams.delete('game');
            window.history.replaceState(history.state, '', url.pathname + url.search);
            void openGameDetailModal(gameParam);
        }
    })();

    // «Менеджер матча» передал черновик — сразу открываем форму новой игры.
    if (params.get('create') === 'match') {
        const draft = takeMatchPrefill();
        // Убираем параметр, чтобы F5 не открывал форму заново.
        const url = new URL(window.location.href);
        url.searchParams.delete('create');
        window.history.replaceState(history.state, '', url.pathname + url.search);
        if (draft) {
            void ensureRolesLoaded().then(() => {
                if (state.user?.is_moderator || state.user?.is_admin) {
                    void openCreateGameModal(undefined, undefined, draft);
                } else {
                    alert(t('games.match_add_forbidden'));
                }
            });
        }
    }
}

function updateCreateButtonVisibility(): void {
    const createBtn = document.getElementById('btn-create-game');
    const replayBtn = document.getElementById('btn-add-from-replay');
    const folderBtn = document.getElementById('btn-games-replays');
    const allowed = Boolean(state.user?.is_moderator || state.user?.is_admin);
    if (createBtn) {
        if (allowed) createBtn.removeAttribute('hidden');
        else createBtn.setAttribute('hidden', '');
    }
    if (replayBtn) {
        if (allowed) replayBtn.removeAttribute('hidden');
        else replayBtn.setAttribute('hidden', '');
    }
    if (folderBtn) {
        if (allowed) folderBtn.removeAttribute('hidden');
        else folderBtn.setAttribute('hidden', '');
    }
}

export function unmountGames(): void {
    abortController?.abort();
    abortController = null;
    loadMoreObserver?.disconnect();
    loadMoreObserver = null;
    hasMoreGames = false;
    isLoadingMore = false;
    userHasScrolled = false;
    replayFolderPanelEl = null;
    replayDuplicateEl = null;
    currentReplayId = null;
    currentReplayModNamespaces = [];
    currentReplayLobbySettings = [];
    currentReplayArcadeMap = null;
    replayLobbySettingsEl = null;
    replayFolderParsing = false;
}

// ============================================================
// Загрузка игр
// ============================================================
/** Время прошлого открытия раздела «Игры» (null — первый визит). */
function readGamesSeenAt(): number | null {
    const raw = localStorage.getItem(GAMES_SEEN_KEY);
    if (!raw) return null;
    const ts = Date.parse(raw);
    return Number.isFinite(ts) ? ts : null;
}

/** Отмечаем раздел просмотренным: следующий заход сравнит игры с этим моментом. */
function markGamesSectionSeen(): void {
    localStorage.setItem(GAMES_SEEN_KEY, new Date().toISOString());
}

/** Игры, появившиеся после прошлого открытия раздела. */
function markNewGames(items: GameListItem[]): void {
    if (gamesSeenAt === null) {
        newGameIds = new Set();
        return;
    }
    newGameIds = new Set(
        items
            .filter((g) => Date.parse(g.played_at) > gamesSeenAt!)
            .map((g) => g.id),
    );
}

/**
 * Плашка «новые игры с прошлого визита»: мягко намекает оценить их
 * реакциями и MVP. Закрывается на время текущего визита.
 */
function renderNewBanner(): void {
    const box = document.getElementById('games-new-banner');
    if (!box) return;
    box.innerHTML = '';

    if (newGameIds.size === 0 || newBannerDismissed) {
        box.classList.add('hidden');
        return;
    }

    const icon = document.createElement('span');
    icon.className = 'games-new-banner-icon';
    icon.textContent = '🔥';

    const texts = document.createElement('div');
    texts.className = 'games-new-banner-texts';

    const title = document.createElement('div');
    title.className = 'games-new-banner-title';
    title.textContent = t('games.new_banner_title', { n: newGameIds.size });

    const hint = document.createElement('div');
    hint.className = 'games-new-banner-hint';
    hint.textContent = state.token
        ? t('games.new_banner_hint')
        : t('games.new_banner_hint_anon');

    texts.append(title, hint);

    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'games-new-banner-close';
    dismiss.title = t('games.new_banner_dismiss');
    dismiss.setAttribute('aria-label', t('games.new_banner_dismiss'));
    dismiss.textContent = '✕';
    dismiss.addEventListener('click', () => {
        newBannerDismissed = true;
        box.classList.add('hidden');
    });

    box.append(icon, texts, dismiss);
    box.classList.remove('hidden');
}

/** Одна страница списка игр (с учётом фильтра по игроку). */
async function fetchGamesPage(offset: number): Promise<GameListItem[]> {
    const params = new URLSearchParams({
        limit: String(GAMES_PAGE_SIZE),
        offset: String(offset),
    });
    if (filters.playerId !== null) params.set('player_id', String(filters.playerId));
    const res = await apiRequest<GamesListResponse>(`/api/games?${params.toString()}`);
    return res.games;
}

async function loadGames(): Promise<void> {
    const container = document.getElementById('games-list-container');
    const errorBox = document.getElementById('games-error');
    if (!container) return;

    container.innerHTML = '<div class="skeleton skeleton-block"></div>';
    errorBox?.classList.add('hidden');
    cachedGames = [];
    hasMoreGames = false;

    try {
        const page = await fetchGamesPage(0);
        cachedGames = page;
        hasMoreGames = page.length === GAMES_PAGE_SIZE;
        markNewGames(page);
        renderNewBanner();
        renderGames();
    } catch (err) {
        if (errorBox) {
            errorBox.textContent = t('games.load_error') +
                (err instanceof Error ? err.message : String(err));
            errorBox.classList.remove('hidden');
        }
        container.innerHTML = '';
    } finally {
        updateLoadMoreUI();
    }
}

/** Подгружает следующую страницу более ранних игр и дописывает её в список. */
async function loadMoreGames(): Promise<void> {
    if (isLoadingMore || !hasMoreGames) return;
    isLoadingMore = true;
    updateLoadMoreUI();
    try {
        const page = await fetchGamesPage(cachedGames.length);
        hasMoreGames = page.length === GAMES_PAGE_SIZE;
        appendLoadedGames(page);
    } catch (err) {
        console.warn('[games] load more failed:', err);
    } finally {
        isLoadingMore = false;
        updateLoadMoreUI();
        // Сентинел мог остаться в зоне видимости — тогда догружаем ещё.
        maybeAutoLoadMore();
    }
}

/** Показывает/скрывает блок подгрузки и переключает текст кнопки. */
function updateLoadMoreUI(): void {
    const box = document.getElementById('games-load-more');
    if (!box) return;
    box.classList.toggle('hidden', !hasMoreGames);
    const btn = document.getElementById('btn-load-more') as HTMLButtonElement | null;
    if (btn) {
        btn.disabled = isLoadingMore;
        btn.textContent = isLoadingMore ? t('games.loading_more') : t('games.load_more');
    }
}

/** Если сентинел всё ещё виден — запускаем следующую подгрузку (инфинити-скролл). */
function maybeAutoLoadMore(): void {
    if (isLoadingMore || !hasMoreGames || !userHasScrolled) return;
    const box = document.getElementById('games-load-more');
    if (!box || box.classList.contains('hidden')) return;
    if (box.getBoundingClientRect().top < window.innerHeight + 400) {
        void loadMoreGames();
    }
}

/**
 * Дописывает новые (более ранние) игры в DOM, не перерисовывая весь список.
 * Затрагиваются только день-группы, в которые попали новые игры.
 */
function appendLoadedGames(newItems: GameListItem[]): void {
    if (newItems.length === 0) return;
    cachedGames.push(...newItems);

    const container = document.getElementById('games-list-container');
    if (!container || !container.querySelector('.game-day-group')) {
        renderGames();
        return;
    }

    const groups = groupGamesByDay(getFilteredGames());
    const order = groups.map((g) => g.dateKey);
    const byKey = new Map(groups.map((g) => [g.dateKey, g] as const));

    for (const key of new Set(newItems.map((g) => localDateKey(g.played_at)))) {
        const group = byKey.get(key);
        if (!group) continue;
        const existing = container.querySelector<HTMLElement>(
            `.game-day-group[data-day-key="${key}"]`
        );
        const node = buildDayGroup(group, order[0] === key);
        if (existing) existing.replaceWith(node);
        else insertDayGroupInOrder(container, node, key, order);
    }
}

/** Подгружает страницы, пока игра не окажется в списке (для подсветки). */
async function highlightGameTillFound(gameId: number): Promise<void> {
    if (!Number.isInteger(gameId)) return;
    let guard = 0;
    while (guard++ < 200) {
        const container = document.getElementById('games-list-container');
        if (container?.querySelector(`[data-game-id="${gameId}"]`)) {
            highlightGame(gameId);
            return;
        }
        if (!hasMoreGames) return;
        const before = cachedGames.length;
        await loadMoreGames();
        if (cachedGames.length === before) return;
    }
}

function hasActiveFilters(): boolean {
    return filters.formatId !== null
        || filters.hostId !== null
        || filters.mapId !== null
        || filters.mapDeleted
        || filters.modName !== null
        || filters.playerId !== null;
}

async function loadPlayerFilterName(id: number): Promise<void> {
    try {
        const res = await apiRequest<{ player: PlayerWithStats }>(`/api/players/${id}`);
        if (filters.playerId === id) {
            playerFilterName = res.player.name + (res.player.aka ? ` (${res.player.aka})` : '');
        }
    } catch {
        playerFilterName = null;
    }
    renderActiveFilterChips();
}

function renderGames(): void {
    const container = document.getElementById('games-list-container');
    if (!container) return;

    const filtered = getFilteredGames();

    if (filtered.length === 0) {
        container.innerHTML = `<p class="hint">${searchQuery || hasActiveFilters() ? t('games.nothing_found') : t('games.no_games')
            }</p>`;
        updateLoadMoreUI();
        return;
    }

    // Группируем по дню
    const groups = groupGamesByDay(filtered);

    container.innerHTML = '';
    groups.forEach((group, i) => {
        container.appendChild(buildDayGroup(group, i === 0));
    });
    updateLoadMoreUI();
}

/** Игры с учётом активных фильтров и поиска. */
function getFilteredGames(): GameListItem[] {
    let filtered = cachedGames;

    if (filters.formatId !== null) filtered = filtered.filter((g) => g.format_id === filters.formatId);
    if (filters.hostId !== null) filtered = filtered.filter((g) => g.host_id === filters.hostId);
    if (filters.mapDeleted) {
        const deleted = deletedMapIds();
        filtered = filtered.filter((g) => g.map_id !== null && deleted.has(g.map_id));
    } else if (filters.mapId !== null) {
        filtered = filtered.filter((g) => g.map_id === filters.mapId);
    }
    if (filters.modName !== null) filtered = filtered.filter((g) => g.mod_name === filters.modName);

    if (searchQuery) {
        filtered = filtered.filter((g) => {
            const hay = [
                ...g.winners,
                ...g.participants.map((p) => p.player_name),
                g.map_name ?? '',
                g.host_name ?? '',
                g.mod_name ?? '',
            ].join(' ').toLowerCase();
            return hay.includes(searchQuery);
        });
    }

    return filtered;
}

/** Локальный ключ дня (YYYY-MM-DD) — совпадает с группировкой в списке. */
function localDateKey(playedAt: string | Date): string {
    const d = playedAt instanceof Date ? playedAt : new Date(playedAt);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Относительное время: «5 часов назад», «только что» и т.п. */
function timeAgo(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return t('games.time_ago_just_now');
    if (min < 60) return t('games.time_ago_minutes', { n: String(min) });
    const h = Math.floor(min / 60);
    if (h < 24) return t('games.time_ago_hours', { n: String(h) });
    const d = Math.floor(h / 24);
    return t('games.time_ago_days', { n: String(d) });
}

/** Преобразует полную игру (из API) в элемент списка. */
function gameFullToListItem(gf: GameFull): GameListItem {
    const players = gf.players ?? [];
    return {
        id: gf.id,
        played_at: gf.played_at,
        duration_min: gf.duration_min,
        format_id: gf.format?.id ?? null,
        format_name: gf.format?.name ?? null,
        format_slug: gf.format?.slug ?? null,
        is_team: gf.format?.is_team ?? false,
        host_id: gf.host?.id ?? null,
        host_name: gf.host?.name ?? null,
        map_id: gf.map?.id ?? null,
        map_name: gf.map?.name ?? null,
        mod_name: gf.mod?.name ?? null,
        created_by: gf.created_by,
        created_by_username: gf.created_by_username,
        created_at: gf.created_at,
        updated_at: gf.updated_at,
        player_count: players.length,
        winners: players.filter((p) => p.is_winner).map((p) => p.player_name),
        participants: players.map((p) => ({
            player_id: p.player_id,
            player_name: p.player_name,
            player_aka: p.player_aka,
            race: p.race,
            team: p.team,
            is_winner: p.is_winner,
            eliminated_at: p.eliminated_at,
            elo_delta: p.elo_delta,
            activity_delta: p.activity_delta,
        })),
    };
}

/** Вставляет day-group в правильную позицию (список отсортирован по дате desc). */
function insertDayGroupInOrder(
    container: HTMLElement,
    node: HTMLElement,
    key: string,
    order: string[]
): void {
    const idx = order.indexOf(key);
    const nodes = [...container.querySelectorAll<HTMLElement>('.game-day-group')];
    let before: HTMLElement | null = null;
    for (const n of nodes) {
        const nk = n.dataset.dayKey;
        if (!nk) continue;
        if (order.indexOf(nk) > idx) {
            before = n;
            break;
        }
    }
    container.insertBefore(node, before);
}

/**
 * Обновляет только карточку изменённой игры (без полного перерендера списка).
 * Перерисовываются лишь затронутые день-группы (старая и новая).
 */
function applyEditedGame(gf: GameFull): void {
    const container = document.getElementById('games-list-container');
    if (!container) return;

    const idx = cachedGames.findIndex((g) => g.id === gf.id);
    if (idx === -1) {
        // Игра не была в загруженном списке — обновляем полным запросом.
        void loadGames();
        return;
    }

    const prevKey = localDateKey(cachedGames[idx]!.played_at);
    const item = gameFullToListItem(gf);
    cachedGames[idx] = item;
    // Сохраняем порядок списка (сервер отдаёт игры по played_at desc).
    cachedGames.sort((a, b) => new Date(b.played_at).getTime() - new Date(a.played_at).getTime());
    const newKey = localDateKey(item.played_at);

    // Если список ещё не отрисован — рисуем целиком.
    if (!container.querySelector('.game-day-group')) {
        renderGames();
        return;
    }

    const groups = groupGamesByDay(getFilteredGames());
    const byKey = new Map(groups.map((g) => [g.dateKey, g] as const));
    const order = groups.map((g) => g.dateKey);

    for (const key of new Set([prevKey, newKey])) {
        const existing = container.querySelector<HTMLElement>(
            `.game-day-group[data-day-key="${key}"]`
        );
        const group = byKey.get(key);
        if (group) {
            const node = buildDayGroup(group, order[0] === key);
            if (existing) existing.replaceWith(node);
            else insertDayGroupInOrder(container, node, key, order);
        } else if (existing) {
            existing.remove();
        }
    }

    // Если после обновления список пуст — показать подсказку.
    if (!container.querySelector('.game-day-group')) {
        container.innerHTML = `<p class="hint">${searchQuery || hasActiveFilters() ? t('games.nothing_found') : t('games.no_games')}</p>`;
    }
}

/** Убирает карточку удалённой игры из списка без полного перерендера. */
function removeGameFromList(id: number): void {
    const container = document.getElementById('games-list-container');
    const idx = cachedGames.findIndex((g) => g.id === id);
    if (idx === -1) return;

    const key = localDateKey(cachedGames[idx]!.played_at);
    cachedGames.splice(idx, 1);
    if (!container) return;

    if (!container.querySelector('.game-day-group')) {
        renderGames();
        return;
    }

    const groups = groupGamesByDay(getFilteredGames());
    const group = groups.find((g) => g.dateKey === key) ?? null;
    const existing = container.querySelector<HTMLElement>(`.game-day-group[data-day-key="${key}"]`);

    if (group) {
        const node = buildDayGroup(group, groups[0]?.dateKey === key);
        if (existing) existing.replaceWith(node);
        else insertDayGroupInOrder(container, node, key, groups.map((g) => g.dateKey));
    } else if (existing) {
        existing.remove();
    }

    // Если после удаления список пуст — показать подсказку.
    if (!container.querySelector('.game-day-group')) {
        container.innerHTML = `<p class="hint">${searchQuery || hasActiveFilters() ? t('games.nothing_found') : t('games.no_games')}</p>`;
    }
}

interface DayGroup {
    dateKey: string;       // YYYY-MM-DD
    dateObj: Date;
    games: GameListItem[];
    hosts: string[];       // уникальные проводящие
}

function groupGamesByDay(games: GameListItem[]): DayGroup[] {
    const map = new Map<string, DayGroup>();

    for (const g of games) {
        const d = new Date(g.played_at);
        // Ключ — локальная дата (не UTC), чтобы соответствовать тому, что видит пользователь
        const dateKey = localDateKey(d);

        let group = map.get(dateKey);
        if (!group) {
            group = { dateKey, dateObj: d, games: [], hosts: [] };
            map.set(dateKey, group);
        }
        group.games.push(g);
        if (g.host_name && !group.hosts.includes(g.host_name)) {
            group.hosts.push(g.host_name);
        }
    }

    // Сортируем группы по дате (от новых к старым)
    const groups = [...map.values()];
    groups.sort((a, b) => b.dateObj.getTime() - a.dateObj.getTime());

    return groups;
}

/** Свёрнутые дни (пользовательские переключения) и уже встречавшиеся дни. */
const collapsedDays = new Set<string>();
const knownDays = new Set<string>();

/** По умолчанию свёрнуты все дни, кроме самого свежего. */
function isDayCollapsed(key: string, isLatest: boolean): boolean {
    if (!knownDays.has(key)) {
        knownDays.add(key);
        if (!isLatest) collapsedDays.add(key);
    }
    return collapsedDays.has(key);
}

function buildDayGroup(group: DayGroup, isLatest: boolean): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'game-day-group';
    wrap.dataset.dayKey = group.dateKey;
    if (isDayCollapsed(group.dateKey, isLatest)) wrap.classList.add('is-collapsed');

    // Заголовок (кликабельный — сворачивает/разворачивает игры дня)
    const header = document.createElement('div');
    header.className = 'game-day-header game-day-header--toggle';
    header.setAttribute('role', 'button');
    header.tabIndex = 0;

    const chevron = document.createElement('span');
    chevron.className = 'game-day-chevron';
    chevron.textContent = '▸';
    chevron.setAttribute('aria-hidden', 'true');

    const date = document.createElement('div');
    date.className = 'game-day-date';
    date.textContent = formatDayHeader(group.dateObj);

    const count = document.createElement('span');
    count.className = 'game-day-count';
    count.textContent = String(group.games.length);

    const hosts = document.createElement('div');
    hosts.className = 'game-day-hosts';
    for (const hostName of group.hosts) {
        const badge = document.createElement('span');
        badge.className = 'game-day-host-badge';
        badge.textContent = `🎤 ${hostName}`;
        hosts.appendChild(badge);
    }

    const left = document.createElement('div');
    left.className = 'game-day-header-left';
    left.append(chevron, date, count);

    header.append(left, hosts);

    const toggle = () => {
        const nowCollapsed = wrap.classList.toggle('is-collapsed');
        if (nowCollapsed) collapsedDays.add(group.dateKey);
        else collapsedDays.delete(group.dateKey);
    };
    header.addEventListener('click', toggle);
    header.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
        }
    });

    wrap.appendChild(header);

    // Игры внутри дня. Сворачиваем через grid-template-rows: 0fr → 1fr
    // (плавно, как раскрытие списка игроков). Контент остаётся в DOM,
    // поэтому браузерный Ctrl+F всё равно находит скрытые игры.
    const list = document.createElement('div');
    list.className = 'game-day-list';
    const inner = document.createElement('div');
    inner.className = 'game-day-list-inner';
    for (const g of group.games) {
        inner.appendChild(buildGameCard(g));
    }
    list.appendChild(inner);
    wrap.appendChild(list);

    return wrap;
}

function formatDayHeader(d: Date): string {
    const locale = getLocale() === 'ru' ? 'ru-RU' : 'en-US';
    return d.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}

// ============================================================
// Карточка игры
// ============================================================
function buildGameCard(g: GameListItem): HTMLElement {
    const card = document.createElement('div');
    card.className = 'game-card';
    card.dataset.gameId = String(g.id);
    if (g.is_team) card.classList.add('is-team');

    const header = document.createElement('div');
    header.className = 'game-card-header';

    const leftMeta = document.createElement('div');
    leftMeta.className = 'game-card-left';

    const date = document.createElement('span');
    date.className = 'game-card-date';
    const d = new Date(g.played_at);
    date.textContent = d.toLocaleString(getLocale() === 'ru' ? 'ru-RU' : 'en-US', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
    leftMeta.appendChild(date);

    if (g.format_name) {
        const format = document.createElement('span');
        format.className = 'game-format-badge';
        if (g.is_team) format.classList.add('is-team');
        format.textContent = g.format_name + (g.is_team ? ` · ${t('games.team_label')}` : '');
        format.title = t('games.click_to_filter');
        format.addEventListener('click', (e) => {
            e.stopPropagation();
            if (g.format_id) setFilter('format', g.format_id);
        });
        leftMeta.appendChild(format);
    }

    // Метка «новое» — игра появилась после прошлого открытия раздела.
    if (newGameIds.has(g.id)) {
        card.classList.add('is-new');
        const badge = document.createElement('span');
        badge.className = 'game-new-badge';
        badge.textContent = `✦ ${t('games.new_badge')}`;
        badge.title = t('games.new_badge_hint');
        leftMeta.appendChild(badge);
    }

    header.appendChild(leftMeta);

    if (g.duration_min) {
        const duration = document.createElement('span');
        duration.className = 'game-card-duration';
        duration.textContent = `${g.duration_min} ${t('games.minutes_short')}`;
        header.appendChild(duration);
    }

    card.appendChild(header);

    const meta = document.createElement('div');
    meta.className = 'game-card-meta';

    if (g.map_name) {
        const deleted = g.map_id !== null && deletedMapIds().has(g.map_id);
        const mapEl = document.createElement('span');
        if (deleted) {
            mapEl.className = 'game-map-deleted';
            mapEl.textContent = `🗑 ${t('games.map_deleted')}: ${g.map_name}`;
            mapEl.title = t('games.map_deleted_hint');
        } else {
            mapEl.className = 'game-clickable';
            mapEl.textContent = g.map_name;
            mapEl.title = t('games.click_to_filter');
            mapEl.addEventListener('click', (e) => {
                e.stopPropagation();
                if (g.map_id) setFilter('map', g.map_id);
            });
        }
        meta.appendChild(mapEl);
    }

    if (g.mod_name) {
        if (meta.childNodes.length > 0) meta.appendChild(document.createTextNode(' · '));
        const modEl = document.createElement('span');
        modEl.className = 'game-clickable';
        modEl.textContent = g.mod_name;
        modEl.title = t('games.click_to_filter');
        modEl.addEventListener('click', (e) => {
            e.stopPropagation();
            if (g.mod_name) setFilter('mod', -1, g.mod_name);
        });
        meta.appendChild(modEl);
    }

    if (g.host_name) {
        if (meta.childNodes.length > 0) meta.appendChild(document.createTextNode(' · '));
        meta.appendChild(document.createTextNode(`${t('games.host_label')}: `));
        const hostEl = document.createElement('span');
        hostEl.className = 'game-clickable';
        hostEl.textContent = g.host_name;
        hostEl.title = t('games.click_to_filter');
        hostEl.addEventListener('click', (e) => {
            e.stopPropagation();
            if (g.host_id) setFilter('host', g.host_id);
        });
        meta.appendChild(hostEl);
    }

    card.appendChild(meta);

    const playersSummary = document.createElement('div');
    playersSummary.className = 'game-card-players-summary';

    // --- Победители / сводка ---
    if (g.is_team) {
        buildTeamSummary(playersSummary, g);
    } else {
        buildSoloSummary(playersSummary, g);
    }

    card.appendChild(playersSummary);

    // --- Раскрывающийся список всех участников ---
    const detailsWrap = document.createElement('div');
    detailsWrap.className = 'game-card-players-details';

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'game-card-players-toggle';
    toggle.textContent = `${t('games.show_all_players')} (${g.player_count})`;

    const allListWrap = document.createElement('div');
    allListWrap.className = 'game-card-players-expandable';

    const allList = document.createElement('div');
    allList.className = 'game-card-players-all';

    if (g.is_team) {
        renderTeamPlayers(allList, g);
    } else {
        renderSoloPlayers(allList, g);
    }

    allListWrap.appendChild(allList);

    toggle.addEventListener('click', () => {
        const open = detailsWrap.classList.toggle('is-open');
        toggle.textContent = open
            ? `${t('games.hide_all_players')} (${g.player_count})`
            : `${t('games.show_all_players')} (${g.player_count})`;
    });

    detailsWrap.append(toggle, allListWrap);
    card.appendChild(detailsWrap);

    // Реакции и голос за MVP — тот же блок, что в модалке игры, но компактно.
    card.appendChild(
        buildReactionsBlock(
            g.id,
            g.participants.map((p) => ({
                player_id: p.player_id,
                player_name: p.player_name,
            })),
            { compact: true },
        ),
    );

    const footer = document.createElement('div');
    footer.className = 'game-card-footer';

    const canViewMeta = Boolean(state.user?.is_moderator || state.user?.is_admin);
    // Превью игры в OBS-оверлее — любому залогиненному (нужен JWT)
    const canOverlay = Boolean(state.token && state.user);

    if (g.created_by_username) {
        const creator = document.createElement('span');
        creator.className = 'game-card-creator';
        let text = `${t('games.added_by')}: ${g.created_by_username}`;
        // Показываем относительное время: «изменено N назад», либо «создано N назад».
        if (canViewMeta && g.updated_at && g.created_at && g.updated_at !== g.created_at) {
            text += ` · ${t('games.edited_at')}: ${timeAgo(g.updated_at)}`;
        } else if (g.created_at) {
            text += ` · ${t('games.created_at')}: ${timeAgo(g.created_at)}`;
        }
        creator.textContent = text;
        footer.appendChild(creator);
    }

    // Кнопки действий: скачивание доступно всем, остальное — по ролям.
    const actions = document.createElement('div');
    actions.className = 'game-card-actions';

    const shareBtn = document.createElement('button');
    shareBtn.type = 'button';
    shareBtn.className = 'game-action-btn game-action-btn--download';
    shareBtn.title = t('share.download_hint');
    shareBtn.setAttribute('aria-label', t('share.button'));
    shareBtn.textContent = '⤓';
    shareBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        void downloadGameCard(g.id);
    });
    actions.appendChild(shareBtn);

    if (canOverlay) {
        actions.appendChild(
            buildGameOverlayButton(g.id, 'game-action-btn game-action-btn--overlay', '📺')
        );
    }

    if (canViewMeta) {
        const editBtn = document.createElement('button');
        editBtn.type = 'button';
        editBtn.className = 'game-action-btn game-action-btn--edit';
        editBtn.title = t('games.edit_game');
        editBtn.textContent = '✎';
        editBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            void openEditGameModal(g.id);
        });

        // Редактировать может админ или модератор, добавивший игру сам.
        const isAdmin = Boolean(state.user?.is_admin);
        const isOwner = state.user?.id && g.created_by === state.user.id;
        if (isAdmin || isOwner) {
            actions.appendChild(editBtn);
        }

        const dupBtn = document.createElement('button');
        dupBtn.type = 'button';
        dupBtn.className = 'game-action-btn game-action-btn--duplicate';
        dupBtn.title = t('games.duplicate_settings');
        dupBtn.textContent = '➕';
        dupBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            void openCreateFromGame(g.id);
        });

        actions.append(dupBtn);

        // Удалять может админ или модератор, добавивший игру сам.
        if (isAdmin || isOwner) {
            const delBtn = document.createElement('button');
            delBtn.type = 'button';
            delBtn.className = 'game-action-btn game-action-btn--delete';
            delBtn.title = t('games.delete_game');
            delBtn.textContent = '🗑';
            delBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                void deleteGame(g);
            });
            actions.appendChild(delBtn);
        }
    }

    footer.appendChild(actions);

    card.appendChild(footer);

    return card;
}

/** Скачать картинку-итог игры: полные данные берём запросом по id. */
async function downloadGameCard(gameId: number): Promise<void> {
    try {
        const res = await apiRequest<{ game: GameFull }>(`/api/games/${gameId}`);
        if (res.game) await shareGameCard(res.game);
    } catch (err) {
        console.warn('[games] share failed:', err);
    }
}

/**
 * Сводка по одиночной игре: показывает только победителя (или «никто»).
 */
function buildSoloSummary(container: HTMLElement, g: GameListItem): void {
    const winners = g.participants.filter((p) => p.is_winner);
    if (winners.length === 0) {
        const empty = document.createElement('span');
        empty.className = 'game-player-summary-empty';
        empty.textContent = t('games.no_winner');
        container.appendChild(empty);
        return;
    }

    for (const p of winners) {
        const el = document.createElement('span');
        el.className = 'game-player is-winner';
        el.style.setProperty('--race-color', getRaceColor(p.race));

        const medal = document.createElement('span');
        medal.className = 'game-player-medal';
        medal.textContent = '🥇';

        const race = buildRaceIcon(p.race, 'game-player-race');

        const name = document.createElement('span');
        name.className = 'game-player-name';
        name.textContent = p.player_name;
        makePlayerNameClickable(name, p.player_id);

        el.append(medal, race, name);
        container.appendChild(el);
    }
}

/**
 * Сводка по командной игре: показывает команду-победителя и её состав,
 * а рядом — общее число команд.
 */
function buildTeamSummary(container: HTMLElement, g: GameListItem): void {
    // Группируем по team
    const teamMap = new Map<number, typeof g.participants>();
    for (const p of g.participants) {
        const tNum = p.team ?? 0;
        const arr = teamMap.get(tNum) ?? [];
        arr.push(p);
        teamMap.set(tNum, arr);
    }

    // Ищем команду-победителя
    const winnerEntry = [...teamMap.entries()].find(([_, players]) =>
        players.some((p) => p.is_winner)
    );

    if (!winnerEntry) {
        const empty = document.createElement('span');
        empty.className = 'game-player-summary-empty';
        empty.textContent = t('games.no_winner');
        container.appendChild(empty);
        return;
    }

    const [, winnerPlayers] = winnerEntry;

    const teamEl = document.createElement('span');
    teamEl.className = 'game-team is-winner';

    const medal = document.createElement('span');
    medal.className = 'game-player-medal';
    medal.textContent = '🥇';
    teamEl.appendChild(medal);

    const members = document.createElement('span');
    members.className = 'game-team-members';

    winnerPlayers.forEach((p, i) => {
        if (i > 0) {
            const sep = document.createElement('span');
            sep.className = 'game-player-sep';
            sep.textContent = '+';
            members.appendChild(sep);
        }

        const pEl = document.createElement('span');
        pEl.className = 'game-player';
        pEl.style.setProperty('--race-color', getRaceColor(p.race));

        const race = buildRaceIcon(p.race, 'game-player-race');

        const name = document.createElement('span');
        name.className = 'game-player-name';
        name.textContent = p.player_name;
        makePlayerNameClickable(name, p.player_id);

        pEl.append(race, name);
        members.appendChild(pEl);
    });

    teamEl.appendChild(members);
    container.appendChild(teamEl);

    // Доп. инфо: сколько всего команд
    const teamsCount = document.createElement('span');
    teamsCount.className = 'game-team-count';
    teamsCount.textContent = `${teamMap.size} ${t('games.teams_short')}`;
    container.appendChild(teamsCount);
}

/** Бейдж дельт Elo и activity за игру (из rating_history). */
function buildDeltaBadge(p: GameListItem['participants'][number]): HTMLElement | null {
    if (p.elo_delta == null && p.activity_delta == null) return null;
    const badge = document.createElement('span');
    badge.className = 'player-line-delta';

    if (p.elo_delta != null) {
        const elo = document.createElement('span');
        elo.className = 'player-delta-elo' + (p.elo_delta >= 0 ? ' is-plus' : ' is-minus');
        elo.textContent = `${p.elo_delta >= 0 ? '+' : ''}${p.elo_delta}`;
        badge.appendChild(elo);
    }

    if (p.activity_delta != null) {
        const act = document.createElement('span');
        act.className = 'player-delta-activity';
        const v = Number(p.activity_delta);
        act.textContent = `${v >= 0 ? '+' : ''}${v.toFixed(1)}`;
        badge.appendChild(act);
    }

    return badge;
}

function renderSoloPlayers(container: HTMLElement, g: GameListItem): void {
  const total = g.participants.length;

  // Сортируем по месту (победитель — 1), затем по имени
  const sorted = [...g.participants].sort((a, b) => {
    const pa = entryPlace(a, total) ?? Number.MAX_SAFE_INTEGER;
    const pb = entryPlace(b, total) ?? Number.MAX_SAFE_INTEGER;
    if (pa !== pb) return pa - pb;
    return a.player_name.localeCompare(b.player_name);
  });

  for (const p of sorted) {
    container.appendChild(buildPlayerLine(p, total));
  }
}

/** Строка участника (место + раса + имя + дельты) для списка участников. */
function buildPlayerLine(
    p: GameListItem['participants'][number],
    total: number
): HTMLElement {
    const place = entryPlace(p, total);
    const row = document.createElement('div');
    row.className = 'player-line';
    if (p.is_winner) row.classList.add('is-winner');
    if (!p.is_winner && place !== null) row.classList.add('is-eliminated');
    row.style.setProperty('--race-color', getRaceColor(p.race));

    // Место
    const placeEl = document.createElement('span');
    placeEl.className = 'player-line-place';
    if (place === 1) {
      placeEl.textContent = '🥇';
      placeEl.title = t('games.place_1');
    } else if (place !== null) {
      placeEl.textContent = place === 2 ? '🥈' : place === 3 ? '🥉' : `#${place}`;
      placeEl.title = formatPlace(place);
    } else {
      placeEl.textContent = '—';
      placeEl.title = t('games.not_eliminated');
    }
    row.appendChild(placeEl);

    // Раса
    const raceEl = buildRaceIcon(p.race, 'player-line-race');
    row.appendChild(raceEl);

    // Имя
    const nameEl = document.createElement('span');
    nameEl.className = 'player-line-name';
    nameEl.textContent = p.player_name;
    if (p.player_aka) nameEl.title = `aka ${p.player_aka}`;
    makePlayerNameClickable(nameEl, p.player_id);
    row.appendChild(nameEl);

    const delta = buildDeltaBadge(p);
    if (delta) row.appendChild(delta);

    return row;
}

function renderTeamPlayers(container: HTMLElement, g: GameListItem): void {
  const total = g.participants.length;
  const teamMap = new Map<number, typeof g.participants>();
  for (const p of g.participants) {
    const teamNum = p.team ?? 0;
    const arr = teamMap.get(teamNum) ?? [];
    arr.push(p);
    teamMap.set(teamNum, arr);
  }

  // Лучшее место команды (победители — 1) для сортировки команд.
  const teamBest = (players: typeof g.participants): number => {
    if (players.some((p) => p.is_winner)) return 1;
    const places = players
      .map((p) => entryPlace(p, total))
      .filter((x): x is number => x !== null);
    return places.length ? Math.min(...places) : Number.MAX_SAFE_INTEGER;
  };

  const teams = [...teamMap.entries()].sort((a, b) => {
    const pa = teamBest(a[1]);
    const pb = teamBest(b[1]);
    if (pa !== pb) return pa - pb;
    return a[0] - b[0];
  });

  for (const [teamNum, players] of teams) {
    const isWinner = players.some((p) => p.is_winner);
    const hasEliminated = players.some((p) => p.eliminated_at !== null);

    // Строка-заголовок команды
    const teamRow = document.createElement('div');
    teamRow.className = 'player-team-line';
    if (isWinner) teamRow.classList.add('is-winner');
    if (!isWinner && hasEliminated) teamRow.classList.add('is-eliminated');

    const labelEl = document.createElement('span');
    labelEl.className = 'player-team-label';
    labelEl.textContent = `${t('games.team_label')} ${teamNum}${isWinner ? ' 👑' : ''}`;
    teamRow.appendChild(labelEl);
    container.appendChild(teamRow);

    // Игроки команды — каждый на новой строке с отступом
    const playersEl = document.createElement('div');
    playersEl.className = 'player-team-players';

    players
      .slice()
      .sort((a, b) => {
        const pa = entryPlace(a, total) ?? Number.MAX_SAFE_INTEGER;
        const pb = entryPlace(b, total) ?? Number.MAX_SAFE_INTEGER;
        if (pa !== pb) return pa - pb;
        return a.player_name.localeCompare(b.player_name);
      })
      .forEach((p) => {
        const row = buildPlayerLine(p, total);
        row.classList.add('player-team-player');
        playersEl.appendChild(row);
      });

    container.appendChild(playersEl);
  }
}

function getRaceColor(race: string): string {
    switch (race) {
        case 'T': return 'var(--race-terran)';
        case 'Z': return 'var(--race-zerg)';
        case 'P': return 'var(--race-protoss)';
        case 'R': return 'var(--race-random)';
        default: return 'var(--text-muted)';
    }
}

const RACE_ICON_URL: Record<string, string> = {
    T: terranIconUrl,
    Z: zergIconUrl,
    P: protossIconUrl,
    R: randomIconUrl,
};

function buildRaceIcon(race: string, className: string): HTMLImageElement {
    const img = document.createElement('img');
    img.className = className;
    img.src = RACE_ICON_URL[race] ?? randomIconUrl;
    img.alt = race;
img.loading = 'lazy';
    return img;
}

function highlightGame(gameId: number): void {
    const container = document.getElementById('games-list-container');
    if (!container || !Number.isInteger(gameId)) return;
    const card = container.querySelector<HTMLElement>(`[data-game-id="${gameId}"]`);
    if (!card) return;
    // Если день свёрнут — разворачиваем, чтобы подсвеченная игра была видна.
    const group = card.closest<HTMLElement>('.game-day-group');
    if (group?.classList.contains('is-collapsed')) {
        group.classList.remove('is-collapsed');
        const key = group.dataset.dayKey;
        if (key) collapsedDays.delete(key);
    }
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    card.classList.add('is-highlighted');
    setTimeout(() => card.classList.remove('is-highlighted'), 3000);
}

// ============================================================
// Фильтры
// ============================================================
async function setupFilterSelects(): Promise<void> {
    try {
        if (cachedFormats.length === 0 || cachedHosts.length === 0 || cachedMaps.length === 0) {
            const [formats, hosts, maps] = await Promise.all([
                listRefs<GameFormat>('formats'),
                listRefs<GameHost>('hosts'),
                listRefs<GameMap>('maps'),
            ]);
            cachedFormats = formats;
            cachedHosts = hosts;
            cachedMaps = maps;
        }

        fillFilterSelect('filter-format', cachedFormats);
        fillFilterSelect('filter-host', cachedHosts);
        fillMapFilterSelect();

        const formatSel = document.getElementById('filter-format') as HTMLSelectElement | null;
        const hostSel = document.getElementById('filter-host') as HTMLSelectElement | null;
        const mapSel = document.getElementById('filter-map') as HTMLSelectElement | null;

        if (formatSel && filters.formatId !== null) formatSel.value = String(filters.formatId);
        if (hostSel && filters.hostId !== null) hostSel.value = String(filters.hostId);
        if (mapSel && filters.mapDeleted) mapSel.value = 'deleted';
        else if (mapSel && filters.mapId !== null) mapSel.value = String(filters.mapId);

        renderActiveFilterChips();
    } catch (err) {
        console.error('[games] failed to load refs for filters:', err);
    }
}

function fillFilterSelect(selectId: string, items: { id: number; name: string }[]): void {
    const select = document.getElementById(selectId) as HTMLSelectElement | null;
    if (!select) return;
    const currentValue = select.value;
    select.innerHTML = '';
    const allOpt = document.createElement('option');
    allOpt.value = '';
    allOpt.textContent = t('games.filter_all');
    select.appendChild(allOpt);
    for (const item of items) {
        const opt = document.createElement('option');
        opt.value = String(item.id);
        opt.textContent = item.name;
        select.appendChild(opt);
    }
    select.value = currentValue;
}

/** Фильтр по картам: неудалённые карты + спец-вариант «карта удалена». */
function fillMapFilterSelect(): void {
    const select = document.getElementById('filter-map') as HTMLSelectElement | null;
    if (!select) return;
    const currentValue = select.value;
    select.innerHTML = '';

    const allOpt = document.createElement('option');
    allOpt.value = '';
    allOpt.textContent = t('games.filter_all');
    select.appendChild(allOpt);

    for (const m of activeMaps()) {
        const opt = document.createElement('option');
        opt.value = String(m.id);
        opt.textContent = mapLabel(m);
        select.appendChild(opt);
    }

    const delOpt = document.createElement('option');
    delOpt.value = 'deleted';
    delOpt.textContent = `🗑 ${t('games.map_deleted')}`;
    select.appendChild(delOpt);

    select.value = currentValue;
}

function applyFilters(): void {
    updateUrlFromFilters();
    renderGames();
    renderActiveFilterChips();
}

function resetFilters(): void {
    const hadPlayer = filters.playerId !== null;
    filters.formatId = null;
    filters.hostId = null;
    filters.mapId = null;
    filters.mapDeleted = false;
    filters.modName = null;
    filters.playerId = null;
    playerFilterName = null;
    const formatSel = document.getElementById('filter-format') as HTMLSelectElement | null;
    const hostSel = document.getElementById('filter-host') as HTMLSelectElement | null;
    const mapSel = document.getElementById('filter-map') as HTMLSelectElement | null;
    if (formatSel) formatSel.value = '';
    if (hostSel) hostSel.value = '';
    if (mapSel) mapSel.value = '';

    if (hadPlayer) {
        updateUrlFromFilters();
        renderActiveFilterChips();
        void loadGames();
    } else {
        applyFilters();
    }
}

function removePlayerFilter(): void {
    filters.playerId = null;
    playerFilterName = null;
    updateUrlFromFilters();
    renderActiveFilterChips();
    void loadGames();
}

function updateUrlFromFilters(): void {
    const url = new URL(window.location.href);
    if (filters.formatId !== null) url.searchParams.set('format', String(filters.formatId));
    else url.searchParams.delete('format');
    if (filters.hostId !== null) url.searchParams.set('host', String(filters.hostId));
    else url.searchParams.delete('host');
    if (filters.mapDeleted) url.searchParams.set('map', 'deleted');
    else if (filters.mapId !== null) url.searchParams.set('map', String(filters.mapId));
    else url.searchParams.delete('map');
    if (filters.modName !== null) url.searchParams.set('mod', filters.modName);
    else url.searchParams.delete('mod');
    if (filters.playerId !== null) url.searchParams.set('player_id', String(filters.playerId));
    else url.searchParams.delete('player_id');
    window.history.replaceState({}, '', url.pathname + url.search);
}

function setFilter(type: 'format' | 'host' | 'map' | 'mod', id: number, modName?: string): void {
    if (type === 'format') filters.formatId = id;
    if (type === 'host') filters.hostId = id;
    if (type === 'map') {
        filters.mapId = id;
        filters.mapDeleted = false;
    }
    if (type === 'mod') filters.modName = modName ?? null;
    // Для мода нет селекта в панели — фильтр виден как чип.
    if (type !== 'mod') {
        const selectId = type === 'format' ? 'filter-format' : type === 'host' ? 'filter-host' : 'filter-map';
        const sel = document.getElementById(selectId) as HTMLSelectElement | null;
        if (sel) sel.value = String(id);
    }
    applyFilters();
}

/** Включает фильтр по игроку (как при переходе на «все игры» из карточки игрока). */
function setPlayerFilter(playerId: number): void {
    filters.playerId = playerId;
    playerFilterName = null;
    void loadPlayerFilterName(playerId);
    updateUrlFromFilters();
    renderActiveFilterChips();
    void loadGames();
}

/** Делает имя игрока кликабельным — клик включает фильтр по этому игроку. */
function makePlayerNameClickable(nameEl: HTMLElement, playerId: number): void {
    nameEl.classList.add('game-clickable');
    nameEl.title = t('games.click_to_filter');
    nameEl.addEventListener('click', (e) => {
        e.stopPropagation();
        setPlayerFilter(playerId);
    });
}

function renderActiveFilterChips(): void {
    const container = document.getElementById('games-active-filters');
    const resetBtn = document.getElementById('btn-reset-filters');
    if (!container) return;
    container.innerHTML = '';
    const chips: { label: string; value: string; onRemove: () => void }[] = [];

    if (filters.playerId !== null) {
        chips.push({
            label: t('games.filter_player'),
            value: playerFilterName ?? `#${filters.playerId}`,
            onRemove: () => removePlayerFilter(),
        });
    }

    if (filters.formatId !== null) {
        const f = cachedFormats.find((x) => x.id === filters.formatId);
        if (f) chips.push({
            label: t('games.filter_format'),
            value: f.name,
            onRemove: () => {
                filters.formatId = null;
                const sel = document.getElementById('filter-format') as HTMLSelectElement | null;
                if (sel) sel.value = '';
                applyFilters();
            },
        });
    }

    if (filters.hostId !== null) {
        const h = cachedHosts.find((x) => x.id === filters.hostId);
        if (h) chips.push({
            label: t('games.filter_host'),
            value: h.name,
            onRemove: () => {
                filters.hostId = null;
                const sel = document.getElementById('filter-host') as HTMLSelectElement | null;
                if (sel) sel.value = '';
                applyFilters();
            },
        });
    }

    if (filters.modName !== null) {
        chips.push({
            label: t('games.filter_mod'),
            value: filters.modName,
            onRemove: () => {
                filters.modName = null;
                applyFilters();
            },
        });
    }

    if (filters.mapDeleted) {
        chips.push({
            label: t('games.filter_map'),
            value: t('games.map_deleted'),
            onRemove: () => {
                filters.mapDeleted = false;
                const sel = document.getElementById('filter-map') as HTMLSelectElement | null;
                if (sel) sel.value = '';
                applyFilters();
            },
        });
    }

    if (filters.mapId !== null) {
        const m = cachedMaps.find((x) => x.id === filters.mapId);
        if (m) chips.push({
            label: t('games.filter_map'),
            value: mapLabel(m),
            onRemove: () => {
                filters.mapId = null;
                const sel = document.getElementById('filter-map') as HTMLSelectElement | null;
                if (sel) sel.value = '';
                applyFilters();
            },
        });
    }

    if (resetBtn) resetBtn.hidden = chips.length === 0;

    for (const chip of chips) {
        const el = document.createElement('div');
        el.className = 'filter-chip';
        const label = document.createElement('span');
        label.className = 'filter-chip-label';
        label.textContent = chip.label + ':';
        const value = document.createElement('span');
        value.className = 'filter-chip-value';
        value.textContent = chip.value;
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'filter-chip-remove';
        removeBtn.textContent = '✕';
        removeBtn.addEventListener('click', chip.onRemove);
        el.append(label, value, removeBtn);
        container.appendChild(el);
    }
}

// ============================================================
// Справочники
// ============================================================
async function ensureAllRefsLoaded(): Promise<void> {
    const needReload =
        cachedFormats.length === 0 ||
        cachedHosts.length === 0 ||
        cachedMaps.length === 0 ||
        cachedMods.length === 0;
    if (!needReload) return;

    try {
        const [formats, hosts, maps, mods] = await Promise.all([
            listRefs<GameFormat>('formats'),
            listRefs<GameHost>('hosts'),
            listRefs<GameMap>('maps'),
            listRefs<GameMod>('mods'),
        ]);
        cachedFormats = formats;
        cachedHosts = hosts;
        cachedMaps = maps;
        cachedMods = mods;
    } catch (err) {
        console.error('[games] failed to load refs:', err);
    }
}

async function refreshPlayersCache(): Promise<void> {
    try {
        const res = await apiRequest<PlayersListResponse>('/api/players');
        cachedPlayers = res.players;
    } catch (err) {
        console.error('[games] failed to refresh players:', err);
    }
}

function fillSelect(
    select: HTMLSelectElement | null,
    items: { id: number; name: string }[],
    placeholder: string,
    /** Если задано — пустой вариант становится выбираемым с этим текстом. */
    emptyLabel?: string,
    /** Подпись опции (по умолчанию — name). */
    labelFn?: (item: { id: number; name: string }) => string
): void {
    if (!select) return;
    select.innerHTML = '';
    const empty = document.createElement('option');
    empty.value = '';
    if (emptyLabel !== undefined) {
        empty.textContent = emptyLabel;
    } else {
        empty.disabled = true;
        empty.selected = true;
        empty.textContent = placeholder;
    }
    select.appendChild(empty);
    for (const item of items) {
        const opt = document.createElement('option');
        opt.value = String(item.id);
        opt.textContent = labelFn ? labelFn(item) : item.name;
        select.appendChild(opt);
    }
}

function toDatetimeLocal(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}`
    );
}

function getPlaceSuffix(place: number): string {
    if (getLocale() === 'ru') return '-е место';
    const mod10 = place % 10;
    const mod100 = place % 100;
    if (mod10 === 1 && mod100 !== 11) return 'st place';
    if (mod10 === 2 && mod100 !== 12) return 'nd place';
    if (mod10 === 3 && mod100 !== 13) return 'rd place';
    return 'th place';
}

function formatPlace(place: number): string {
    return `${place}${getPlaceSuffix(place)}`;
}

/**
 * Место из записи участника. `eliminated_at` в БД хранит порядок выбывания
 * (1 — выбыл первым), поэтому место = scale - eliminated_at + 1.
 */
function entryPlace(
    p: { is_winner: boolean; eliminated_at: number | null },
    scale: number
): number | null {
    if (p.eliminated_at === null) return p.is_winner ? 1 : null;
    return scale - p.eliminated_at + 1;
}

/**
 * Обратное преобразование: место → порядок выбывания (для записи в БД).
 * Место 1 (лучшее) хранится как `eliminated_at = scale` (выбыл последним),
 * чтобы не путать его с «место не задано» (`null`).
 */
function placeToElim(place: number | null, scale: number): number | null {
    if (place === null) return null;
    return scale - place + 1;
}

function markDirty(): void { isDirty = true; }

function confirmClose(): boolean {
    if (!isDirty) return true;
    return confirm(t('games.confirm_close'));
}

function isTeamMode(): boolean {
    return Boolean(isTeamCheckbox?.checked);
}

function isTrackElim(): boolean {
    // Места теперь обязательны для всех, кроме победителя, поэтому учёт
    // выбывания всегда включён.
    return true;
}

function updateTeamModeVisibility(): void {
    const enabled = isTeamMode();
    if (teamModeOptions) teamModeOptions.hidden = !enabled;
    // В командном режиме слоты фиксированы — кнопка «Добавить игрока» не нужна.
    const addBtn = document.getElementById('btn-add-player');
    if (addBtn) addBtn.hidden = enabled;
    if (placementHintEl) {
        placementHintEl.textContent = enabled
            ? t('games.placement_hint_team')
            : t('games.placement_hint_solo');
    }
}

// ============================================================
// Сохранение последней игры
// ============================================================
function saveLastGameSettings(): void {
    try {
        const settings: LastGameSettings = {
            format_id: formatSelect?.value ? Number(formatSelect.value) : null,
            host_id: hostSelect?.value ? Number(hostSelect.value) : null,
            map_id: mapSelect?.value ? Number(mapSelect.value) : null,
            mod_id: modSelect?.value ? Number(modSelect.value) : null,
            duration_min: durationInput?.value ? Number(durationInput.value) : null,
            is_team: isTeamMode(),
            team_size: Number(teamSizeSelect?.value ?? '2'),
            team_count: Number(teamCountSelect?.value ?? '2'),
            track_elim: isTrackElim(),
            played_at: playedAtInput?.value ?? null,   // ← НОВОЕ
            players: drafts
                .filter((d) => d.player_id !== null)
                .map((d) => ({
                    player_id: d.player_id,
                    race: d.race,
                    team: d.team,
                })),
        };
        localStorage.setItem(LAST_GAME_KEY, JSON.stringify(settings));
    } catch (err) {
        console.warn('[games] failed to save last game settings:', err);
    }
}

function loadLastGameSettings(): LastGameSettings | null {
    try {
        const raw = localStorage.getItem(LAST_GAME_KEY);
        if (!raw) return null;
        return JSON.parse(raw) as LastGameSettings;
    } catch {
        return null;
    }
}

function clearLastGameSettings(): void {
    try {
        localStorage.removeItem(LAST_GAME_KEY);
    } catch { }
}

// ============================================================
// Создание карты/мода/игрока inline
// ============================================================
/** Подпись карты в списках: «Название · Альт.название». */
function mapLabel(m: { name: string; alt_name?: string | null }): string {
    return m.alt_name ? `${m.name} · ${m.alt_name}` : m.name;
}

/** Карты, доступные для выбора (без мягко удалённых). */
function activeMaps(): GameMap[] {
    return cachedMaps.filter((m) => !m.deleted_at);
}

/** Перестраивает скрытый select опций карт и синхронизирует текст комбобокса. */
function fillMapSelect(): void {
    const selected = mapSelect?.value ?? '';
    fillSelect(mapSelect, activeMaps(), t('games.select_placeholder'), undefined, (m) => mapLabel(m as GameMap));
    if (selected && activeMaps().some((m) => String(m.id) === selected)) {
        mapSelect!.value = selected;
    }
    syncMapInput();
}

/** Устанавливает выбранную карту и синхронизирует поле/кнопки. */
function setSelectedMap(value: string): void {
    if (mapSelect) mapSelect.value = value;
    syncMapInput();
}

/** Текст комбобокса = подпись выбранной карты (пусто, если карта не выбрана). */
function syncMapInput(): void {
    const id = mapSelect?.value ? Number(mapSelect.value) : null;
    const map = id !== null ? cachedMaps.find((m) => m.id === id) : undefined;
    if (mapSearchInput) mapSearchInput.value = map ? mapLabel(map) : '';
    updateMapEditButton();
}

/** Показывает кнопку «✎» только когда выбрана неудалённая карта. */
function updateMapEditButton(): void {
    const btn = document.getElementById('btn-edit-map');
    if (!btn) return;
    const id = mapSelect?.value ? Number(mapSelect.value) : null;
    const map = id !== null ? cachedMaps.find((m) => m.id === id) : undefined;
    if (map && !map.deleted_at) btn.classList.remove('hidden');
    else btn.classList.add('hidden');
}

/** Выпадающий список карт по подстроке (имя или альт. название). */
function showMapDropdown(query: string): void {
    if (!mapDropdown) return;
    const q = query.trim().toLowerCase();
    const selectedId = mapSelect?.value ?? '';
    const maps = activeMaps().filter((m) => !q || mapLabel(m).toLowerCase().includes(q));

    mapHighlight = -1;
    if (maps.length === 0) {
        mapDropdown.innerHTML = `<div class="player-autocomplete-empty">${t('maps.empty')}</div>`;
        mapDropdown.classList.remove('hidden');
        return;
    }

    mapDropdown.innerHTML = '';
    for (const m of maps.slice(0, 60)) {
        const item = document.createElement('div');
        item.className = 'player-autocomplete-item';
        item.dataset.mapId = String(m.id);
        if (String(m.id) === selectedId) item.classList.add('is-highlighted');

        const name = document.createElement('span');
        name.className = 'player-autocomplete-name';
        name.textContent = m.name;
        item.appendChild(name);
        if (m.alt_name) {
            const alt = document.createElement('span');
            alt.className = 'player-autocomplete-aka';
            alt.textContent = ` ${m.alt_name}`;
            item.appendChild(alt);
        }
        item.addEventListener('mousedown', (e) => {
            e.preventDefault();
            selectMapById(m.id);
        });
        mapDropdown.appendChild(item);
    }
    mapDropdown.classList.remove('hidden');
}

function hideMapDropdown(): void {
    mapDropdown?.classList.add('hidden');
    mapHighlight = -1;
}

/** Перемещение подсветки по списку стрелками ↑/↓. */
function moveMapHighlight(delta: number): void {
    if (!mapDropdown) return;
    const items = [...mapDropdown.querySelectorAll<HTMLElement>('.player-autocomplete-item')];
    if (items.length === 0) return;
    mapHighlight = Math.max(0, Math.min(mapHighlight + delta, items.length - 1));
    items.forEach((el, i) => el.classList.toggle('is-highlighted', i === mapHighlight));
    items[mapHighlight]?.scrollIntoView({ block: 'nearest' });
}

/** Выбор карты по id из списка. */
function selectMapById(id: number): void {
    const map = cachedMaps.find((m) => m.id === id);
    if (!map) return;
    setSelectedMap(String(id));
    hideMapDropdown();
    showMapWarning(null);
    markDirty();
}

/** «+»: открыть общую модалку добавления карты и выбрать созданную. */
async function addMapFromModal(prefill?: MapPrefill): Promise<void> {
    const saved = await openAddMapModal(prefill);
    if (!saved) return;
    cachedMaps = [...cachedMaps.filter((m) => m.id !== saved.id), saved];
    cachedMaps.sort((a, b) => a.name.localeCompare(b.name));
    fillMapSelect();
    setSelectedMap(String(saved.id));
    showMapWarning(null);
    hideMapDropdown();
    markDirty();
}

/** «✎»: открыть общую модалку правки выбранной карты. */
/** «Создать карту из реплея»: форма новой карты с данными из Аркады и картинкой из депо. */
async function addMapFromReplay(arcade: ReplayArcadeMap, button: HTMLButtonElement): Promise<void> {
    button.disabled = true;
    let image: File | null = null;
    if (arcade.image) {
        try {
            const res = await apiRequest<{ png: string }>(
                `/api/games/replay-map-image?region=${arcade.image.region}&hash=${arcade.image.hash}`,
                { token: state.token }
            );
            const bytes = Uint8Array.from(atob(res.png), (c) => c.charCodeAt(0));
            image = new File([bytes], 'map.png', { type: 'image/png' });
        } catch {
            // Без картинки форма всё равно полезна — её можно добавить вручную.
        }
    }
    button.disabled = false;
    await addMapFromModal({
        name: arcade.name,
        alt_name: arcade.altName,
        player_count: arcade.playerCount,
        author: arcade.author,
        link: arcade.link,
        image,
    });
}

async function editSelectedMapFromModal(): Promise<void> {
    const id = mapSelect?.value ? Number(mapSelect.value) : null;
    const map = id !== null ? cachedMaps.find((m) => m.id === id) : undefined;
    if (!map) return;
    const saved = await openEditMapModal(map);
    if (!saved) return;
    cachedMaps = cachedMaps.map((m) => (m.id === saved.id ? { ...m, ...saved } : m));
    cachedMaps.sort((a, b) => a.name.localeCompare(b.name));
    fillMapSelect();
    setSelectedMap(String(saved.id));
    hideMapDropdown();
    markDirty();
}

/** Находит неудалённую карту по названию (name или alt_name), нормализованно. */
function findCachedMap(title: string | null | undefined): GameMap | undefined {
    return findReplayMap(cachedMaps, title);
}

/** Множество id мягко удалённых карт. */
function deletedMapIds(): Set<number> {
    return new Set(cachedMaps.filter((m) => m.deleted_at).map((m) => m.id));
}

function showMapWarning(title: string | null, deleted = false): void {
    const el = document.getElementById('field-map-warning');
    if (!el) return;
    if (!title) {
        el.classList.add('hidden');
        el.textContent = '';
        return;
    }
    el.classList.remove('hidden');
    el.textContent = deleted
        ? t('games.map_deleted_warning', { name: title })
        : t('games.replay_map_not_found', { name: title });
    const arcade = currentReplayArcadeMap;
    if (!deleted && arcade) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn-secondary field-warning-action';
        btn.textContent = t('games.replay_map_create');
        btn.addEventListener('click', () => void addMapFromReplay(arcade, btn));
        el.appendChild(btn);
    }
}

async function createModInline(): Promise<void> {
    const name = prompt(t('games.new_mod_prompt'));
    if (name === null) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
        const newMod = await createRef<GameMod>('mods', { name: trimmed }, state.token);
        cachedMods = [...cachedMods, newMod];
        cachedMods.sort((a, b) => a.name.localeCompare(b.name));
        fillSelect(modSelect, cachedMods, t('games.select_placeholder'), t('games.no_mod'));
        if (modSelect) modSelect.value = String(newMod.id);
        markDirty();
    } catch (err) {
        alert(t('games.new_mod_error') + (err instanceof Error ? err.message : String(err)));
    }
}

async function createPlayerInline(): Promise<void> {
    const name = prompt(t('games.new_player_prompt'));
    if (name === null) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
        const res = await apiRequest<{ player: PlayerWithStats }>('/api/players', {
            method: 'POST',
            token: state.token,
            body: { name: trimmed },
        });
        cachedPlayers.push(res.player);
        cachedPlayers.sort((a, b) => a.name.localeCompare(b.name));
        // Перерисовываем список — новые автокомплиты подхватят кэш
        renderPlayerDrafts();
    } catch (err) {
        alert(t('games.new_player_error') + (err instanceof Error ? err.message : String(err)));
    }
}

// ============================================================
// Управление черновиками
// ============================================================
function getTeamSize(): number {
    return Number(teamSizeSelect?.value ?? '2');
}

function getTeamCount(): number {
    return Math.max(1, Number(teamCountSelect?.value ?? '2'));
}

/** Пустой слот команды (без игрока). */
function emptyTeamSlot(team: number): GamePlayerDraft {
    return { player_id: null, race: 'T', team, is_winner: false, place: null };
}

/** Очищает слот от игрока, но сам слот (команду) сохраняет. */
function clearDraftPlayer(draft: GamePlayerDraft): void {
    // В командном режиме победа ставится на всю команду: если очищаем слот
    // победителя, снимаем победителя со всей команды (как uncheck чекбокса),
    // чтобы пустой слот не оставался победителем.
    if (isTeamMode() && draft.is_winner && draft.team !== null) {
        for (const d of drafts) {
            if (d.team === draft.team) d.is_winner = false;
        }
    }
    draft.player_id = null;
    draft.raw_name = null;
    draft.race = 'T';
    draft.is_winner = false;
    draft.place = null;
    draft.from_replay = false;
}

/** Строит сетку слотов, распределяя игроков по их командам (team сохраняется). */
function buildTeamSlots(players: GamePlayerDraft[]): GamePlayerDraft[] {
    const teamSize = getTeamSize();
    const teamCount = getTeamCount();
    const byTeam = new Map<number, GamePlayerDraft[]>();
    for (const d of players) {
        const t = Math.min(Math.max(d.team ?? 1, 1), teamCount);
        const arr = byTeam.get(t);
        if (arr) arr.push(d);
        else byTeam.set(t, [d]);
    }
    const slots: GamePlayerDraft[] = [];
    for (let t = 1; t <= teamCount; t++) {
        const members = byTeam.get(t) ?? [];
        for (let s = 0; s < teamSize; s++) {
            const src = members[s];
            slots.push(src ? { ...src, team: t } : emptyTeamSlot(t));
        }
    }
    return slots;
}

/** При включении командного режима распределяет заполненных игроков по командам подряд. */
function buildTeamSlotsSequential(filled: GamePlayerDraft[]): GamePlayerDraft[] {
    const teamSize = getTeamSize();
    const teamCount = getTeamCount();
    const slots: GamePlayerDraft[] = [];
    let fi = 0;
    for (let t = 1; t <= teamCount; t++) {
        for (let s = 0; s < teamSize; s++) {
            const src = filled[fi];
            if (src) {
                slots.push({ ...src, team: t });
                fi++;
            } else {
                slots.push(emptyTeamSlot(t));
            }
        }
    }
    return slots;
}

/**
 * Приводит drafts к фиксированной сетке слотов (teamCount × teamSize).
 * Слоты нельзя удалить — можно только очистить/заполнить.
 */
function ensureTeamSlots(): void {
    if (!isTeamMode()) {
        for (const d of drafts) d.team = null;
        return;
    }
    drafts = buildTeamSlots(drafts);
}

/** Включение командного режима: перестраиваем сетку, распределяя игроков подряд. */
function enableTeamMode(): void {
    const filled = drafts.filter((d) => d.player_id !== null || d.raw_name);
    drafts = buildTeamSlotsSequential(filled);
}

function addPlayerDraft(): void {
    // В командном режиме слоты фиксированы — добавлять игроков нельзя,
    // только заполнять пустые слоты.
    if (isTeamMode()) return;
    drafts.push({
        player_id: null,
        race: 'T',
        team: null,
        is_winner: false,
        place: null,
    });
    renderPlayerDrafts();
    markDirty();
}

// ============================================================
// Нормализация мест
// ============================================================
function normalizePlaces(): void {
    // В командном режиме победа засчитывается всей команде, но места остаются
    // индивидуальными и не приравниваются к 1.
    if (isTeamMode()) {
        const byTeam = new Map<number, GamePlayerDraft[]>();
        for (const d of drafts) {
            const tn = d.team ?? 0;
            const arr = byTeam.get(tn) ?? [];
            arr.push(d);
            byTeam.set(tn, arr);
        }
        for (const team of byTeam.values()) {
            if (team.some((p) => p.is_winner)) {
                for (const p of team) p.is_winner = true;
            }
        }
        return;
    }

    for (const d of drafts) {
        if (d.is_winner) d.place = 1;
    }
}

/**
 * Расставляет места по порядку игроков в списке: верхний получает 1-е место,
 * следующий — 2-е и т.д. В одиночном режиме 1-е место означает победителя.
 * Пустые слоты командного режима место не получают.
 */
function autoAssignPlaces(): void {
    const teamMode = isTeamMode();

    // Порядок отображения: одиночный — по текущему месту (незаданные внизу),
    // командный — сгруппировано по командам.
    let order: number[];
    if (teamMode) {
        order = [];
        const teamCount = getTeamCount();
        for (let tn = 1; tn <= teamCount; tn++) {
            drafts.forEach((d, i) => {
                if (d.team === tn) order.push(i);
            });
        }
    } else {
        order = drafts
            .map((_, i) => i)
            .sort((a, b) => {
                const pa = drafts[a]?.place ?? Number.MAX_SAFE_INTEGER;
                const pb = drafts[b]?.place ?? Number.MAX_SAFE_INTEGER;
                return pa !== pb ? pa - pb : a - b;
            });
    }

    let place = 1;
    for (const i of order) {
        const d = drafts[i];
        if (!d) continue;
        if (d.player_id === null && !d.raw_name) {
            d.place = null;   // пустой слот — без места
            continue;
        }
        d.place = place++;
    }

    if (!teamMode) {
        // 1-е место = победитель (в одиночном режиме).
        for (const d of drafts) d.is_winner = d.place === 1;
    }

    renderPlayerDrafts();
    markDirty();
}

// ============================================================
// Список участников в форме
// ============================================================
/** Порядок отображения в одиночном режиме: по месту, без места — в конце. */
function soloVisualOrder(): number[] {
    return drafts
        .map((_, i) => i)
        .sort((a, b) => {
            const pa = drafts[a]?.place ?? Number.MAX_SAFE_INTEGER;
            const pb = drafts[b]?.place ?? Number.MAX_SAFE_INTEGER;
            return pa !== pb ? pa - pb : a - b;
        });
}

/** Слот заполнен (сопоставленный игрок или введённый ник). */
function isFilledDraft(d: GamePlayerDraft | undefined): boolean {
    return !!d && (d.player_id !== null || (d.raw_name ?? '').trim() !== '');
}

/**
 * Сдвиг игрока вверх/вниз в одиночном режиме.
 * Если места уже расставлены — они пересчитываются по новому порядку,
 * чтобы удобно исправлять ошибки после импорта реплея.
 */
function moveDraft(index: number, dir: -1 | 1): void {
    if (isTeamMode()) return;
    const order = soloVisualOrder();
    const pos = order.indexOf(index);
    const targetPos = pos + dir;
    if (pos < 0 || targetPos < 0 || targetPos >= order.length) return;

    const newOrder = order.slice();
    const a = newOrder[pos]!;
    const b = newOrder[targetPos]!;
    newOrder[pos] = b;
    newOrder[targetPos] = a;
    drafts = newOrder.map((i) => drafts[i]!);

    // Если места уже указаны — перенумеровываем по новому порядку.
    if (drafts.some((d) => isFilledDraft(d) && d.place !== null)) {
        let place = 1;
        for (const d of drafts) {
            if (!isFilledDraft(d)) {
                d.place = null;
                continue;
            }
            d.place = place++;
            d.is_winner = d.place === 1;
        }
    }

    renderPlayerDrafts();
    markDirty();
}

function renderPlayerDrafts(): void {
    if (!playersListBox) return;
    playersListBox.innerHTML = '';

    const teamMode = isTeamMode();

    if (teamMode) {
        // Командный режим: drafts — фиксированная сетка слотов (team 1..teamCount).
        const teamCount = getTeamCount();
        for (let tn = 1; tn <= teamCount; tn++) {
            const color = getTeamColor(tn);
            const members: number[] = [];
            drafts.forEach((d, i) => {
                if (d.team === tn) members.push(i);
            });

            const allWinners = members.every((i) => drafts[i]?.is_winner);
            const someWinners = members.some((i) => drafts[i]?.is_winner);

            const group = document.createElement('div');
            group.className = 'team-group';
            if (allWinners) group.classList.add('is-winner');
            group.style.setProperty('--team-color', color);

            // --- Заголовок группы ---
            const header = document.createElement('div');
            header.className = 'team-group-header';

            const headerLabel = document.createElement('span');
            headerLabel.className = 'team-group-label';
            headerLabel.textContent = `${t('games.team_label')} ${tn}`;
            header.appendChild(headerLabel);

            // Победитель / предупреждение
            if (allWinners) {
                const crown = document.createElement('span');
                crown.className = 'team-group-winner';
                crown.textContent = '👑';
                header.appendChild(crown);
            } else if (someWinners) {
                const warn = document.createElement('span');
                warn.className = 'team-group-warning';
                warn.textContent = '⚠';
                warn.title = t('games.error_incomplete_winner_team');
                header.appendChild(warn);
            }

            group.appendChild(header);

            // --- Слоты команды (фиксированные, заполняются автокомплитом) ---
            for (const idx of members) {
                group.appendChild(buildPlayerRow(idx, teamCount, true));
            }

            playersListBox.appendChild(group);
        }
    } else {
        // Одиночный режим: сортируем по выбранному месту (1, 2, …),
        // невыбранные игроки — внизу, сохраняя исходный порядок.
        const order = drafts
            .map((_, i) => i)
            .sort((a, b) => {
                const pa = drafts[a]?.place ?? Number.MAX_SAFE_INTEGER;
                const pb = drafts[b]?.place ?? Number.MAX_SAFE_INTEGER;
                if (pa !== pb) return pa - pb;
                return a - b;
            });
        for (const i of order) {
            const row = buildPlayerRow(i, 0, false);
            playersListBox.appendChild(row);
        }
    }
}

function getTeamColor(teamNum: number): string {
    return TEAM_COLORS[(teamNum - 1) % TEAM_COLORS.length] ?? '#8899aa';
}

/**
 * Строит строку участника.
 */
function buildPlayerRow(index: number, _maxTeams: number, teamMode: boolean): HTMLElement {
    const draft = drafts[index];
    if (!draft) return document.createElement('div');

    // Места считаем по «реальным» игрокам (сопоставленным или с введённым ником),
    // а не по всем слотам: пустые слоты не должны расширять список мест.
    const totalPlayers = drafts.filter(
        (d) => d.player_id !== null || (d.raw_name ?? '').trim() !== ''
    ).length;

    const row = document.createElement('div');
    row.className = 'player-entry';
    if (teamMode) row.classList.add('is-team-mode');
    else row.classList.add('is-solo-mode');
    if (draft.is_winner) row.classList.add('is-winner');

    // --- Автокомплит игрока ---
    const { wrapper: autocompleteWrap } = createPlayerAutocomplete(
        draft,
        () => markDirty(),
        (player) => {
            if (player.dominant_race) {
                draft.race = player.dominant_race;
                setTrigger(player.dominant_race as 'T' | 'Z' | 'P' | 'R');
            }
        }
    );

    // Игрок из реплея ещё не сопоставлен с базой — подсветить
    if (!draft.player_id && draft.raw_name) {
        autocompleteWrap.classList.add('is-unmatched');
        autocompleteWrap.title = t('games.replay_player_unmatched');
    }

    // Условный счёт из реплея — подсказка, по нему отсортирован список.
    if (typeof draft.replay_score === 'number') {
        const scoreEl = document.createElement('span');
        scoreEl.className = 'player-replay-score';
        scoreEl.textContent = formatReplayScore(draft.replay_score);
        scoreEl.title = t('games.replay_score_hint', {
            score: formatReplayScoreFull(draft.replay_score),
            gathered: formatReplayScoreFull(draft.replay_gathered ?? 0),
            killed: formatReplayScoreFull(draft.replay_killed ?? 0),
            lost: formatReplayScoreFull(draft.replay_lost ?? 0),
            army: formatReplayScoreFull(draft.replay_army ?? 0),
            survived: draft.replay_survival !== null && draft.replay_survival !== undefined
                ? formatReplayTime(draft.replay_survival)
                : '—',
        });
        autocompleteWrap.appendChild(scoreEl);
    }

    // --- Раса (выпадающий список с иконками) ---
    const raceOrder: ('T' | 'Z' | 'P' | 'R')[] = ['T', 'Z', 'P', 'R'];

    const racePicker = document.createElement('div');
    racePicker.className = 'race-picker';

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'race-picker-trigger';
    const setTrigger = (r: 'T' | 'Z' | 'P' | 'R'): void => {
        trigger.innerHTML = '';
        trigger.appendChild(buildRaceIcon(r, 'race-picker-icon'));
        trigger.title = r;
    };
    setTrigger(draft.race);

    let menu: HTMLDivElement | null = null;
    const closeMenu = (): void => {
        menu?.remove();
        menu = null;
    };

    const openMenu = (): void => {
        closeMenu();
        menu = document.createElement('div');
        menu.className = 'race-picker-menu';

        const slotH = 34;
        const gap = 4;
        for (const r of raceOrder) {
            const slot = document.createElement('button');
            slot.type = 'button';
            slot.className = 'race-picker-slot' + (draft.race === r ? ' is-active' : '');
            slot.title = r;
            slot.appendChild(buildRaceIcon(r, 'race-picker-icon'));
            if (draft.race === r) {
                // Выбранную расу блокируем — её нельзя выбрать повторно.
                slot.disabled = true;
            } else {
                slot.addEventListener('click', (e) => {
                    e.stopPropagation();
                    setRace(r);
                    markDirty();
                    closeMenu();
                });
            }
            menu.appendChild(slot);
        }

        // Список фиксированный: открывается под триггером, слева выровнен по центру.
        const tr = trigger.getBoundingClientRect();
        const menuW = 40;
        const menuH = raceOrder.length * slotH + (raceOrder.length - 1) * gap + 8;
        let top = tr.bottom + 6;
        let left = tr.left + tr.width / 2 - menuW / 2;
        top = Math.max(8, Math.min(top, window.innerHeight - menuH - 8));
        left = Math.max(8, Math.min(left, window.innerWidth - menuW - 8));
        menu.style.top = `${top}px`;
        menu.style.left = `${left}px`;

        document.body.appendChild(menu);
    };

    const setRace = (r: 'T' | 'Z' | 'P' | 'R'): void => {
        draft.race = r;
        setTrigger(r);
    };

    trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        if (menu) closeMenu();
        else openMenu();
    });

    racePicker.appendChild(trigger);

    // --- Победитель ---
    const winnerWrap = document.createElement('label');
    winnerWrap.className = 'winner-checkbox';
    const winnerCheck = document.createElement('input');
    winnerCheck.type = 'checkbox';
    winnerCheck.checked = draft.is_winner;
    winnerCheck.addEventListener('change', () => {
        draft.is_winner = winnerCheck.checked;
        if (isTeamMode()) {
            // Победа засчитывается всей команде.
            for (const d of drafts) {
                if (d.team !== null && d.team === draft.team) d.is_winner = winnerCheck.checked;
            }
        } else {
            draft.place = winnerCheck.checked ? 1 : null;
        }
        renderPlayerDrafts();
        markDirty();
    });
    winnerWrap.appendChild(winnerCheck);
    const winnerText = document.createElement('span');
    winnerText.textContent = t('games.winner_label');
    winnerWrap.appendChild(winnerText);

    // --- Место (индивидуальное) ---
    let placeSelect: HTMLSelectElement | null = null;
    if (teamMode) {
        // Командный режим: место указывается для каждого игрока отдельно
        // (включая победителя), диапазон 1..N.
        placeSelect = document.createElement('select');
        placeSelect.className = 'elim-select';

        const noPlace = document.createElement('option');
        noPlace.value = '';
        noPlace.textContent = '—';
        placeSelect.appendChild(noPlace);

        const filledCount = drafts.filter((d) => d.player_id !== null).length;
        const maxPlace = Math.max(1, filledCount);
        const usedPlaces = new Set(
            drafts
                .filter((d) => d !== draft && d.place !== null)
                .map((d) => d.place!)
        );

        for (let place = 1; place <= maxPlace; place++) {
            const opt = document.createElement('option');
            opt.value = String(place);
            if (draft.place === place) {
                opt.selected = true;
                opt.textContent = formatPlace(place);
            } else if (usedPlaces.has(place)) {
                opt.disabled = true;
                opt.textContent = `${formatPlace(place)} — ${t('games.used')}`;
            } else {
                opt.textContent = formatPlace(place);
            }
            placeSelect.appendChild(opt);
        }

        if (draft.place === null) placeSelect.value = '';

        const ps = placeSelect;
        ps.addEventListener('change', () => {
            const v = ps.value;
            draft.place = v === '' ? null : Number(v);
            markDirty();
            renderPlayerDrafts();
        });
    } else if (!draft.is_winner) {
        // Одиночный режим: победитель всегда 1, остальные — 2..N.
        placeSelect = document.createElement('select');
        placeSelect.className = 'elim-select';

        const noPlace = document.createElement('option');
        noPlace.value = '';
        noPlace.textContent = '—';
        placeSelect.appendChild(noPlace);

        const maxPlace = Math.max(2, totalPlayers);
        const usedPlaces = new Set(
            drafts
                .filter((d) => d !== draft && !d.is_winner && d.place !== null)
                .map((d) => d.place!)
        );

        for (let place = 2; place <= maxPlace; place++) {
            const opt = document.createElement('option');
            opt.value = String(place);
            if (draft.place === place) {
                opt.selected = true;
                opt.textContent = formatPlace(place);
            } else if (usedPlaces.has(place)) {
                opt.disabled = true;
                opt.textContent = `${formatPlace(place)} — ${t('games.used')}`;
            } else {
                opt.textContent = formatPlace(place);
            }
            placeSelect.appendChild(opt);
        }

        if (draft.place === null || draft.place <= 1) placeSelect.value = '';

        const ps = placeSelect;
        ps.addEventListener('change', () => {
            const v = ps.value;
            draft.place = v === '' ? null : Number(v);
            markDirty();
            renderPlayerDrafts();
        });
    }

    // --- Сдвиг вверх/вниз (только одиночный режим) ---
    let moveGroup: HTMLDivElement | null = null;
    if (!teamMode) {
        const order = soloVisualOrder();
        const pos = order.indexOf(index);

        moveGroup = document.createElement('div');
        moveGroup.className = 'move-player-group';

        const upBtn = document.createElement('button');
        upBtn.type = 'button';
        upBtn.className = 'move-player';
        upBtn.textContent = '▲';
        upBtn.title = t('games.move_up');
        upBtn.disabled = pos <= 0;
        upBtn.addEventListener('click', () => moveDraft(index, -1));

        const downBtn = document.createElement('button');
        downBtn.type = 'button';
        downBtn.className = 'move-player';
        downBtn.textContent = '▼';
        downBtn.title = t('games.move_down');
        downBtn.disabled = pos < 0 || pos >= order.length - 1;
        downBtn.addEventListener('click', () => moveDraft(index, 1));

        moveGroup.append(upBtn, downBtn);
    }

    // --- Удалить / очистить ---
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'remove-player';
    removeBtn.textContent = '✕';
    removeBtn.title = t('games.remove_player');
    removeBtn.addEventListener('click', () => {
        if (teamMode) {
            // В командном режиме слот фиксирован: крестик очищает игрока,
            // но не удаляет сам слот.
            clearDraftPlayer(draft);
        } else {
            drafts.splice(index, 1);
        }
        renderPlayerDrafts();
        markDirty();
    });
    if (teamMode && draft.player_id === null && !draft.raw_name) {
        // Пустой слот — очищать нечего.
        removeBtn.style.visibility = 'hidden';
    }

    row.append(autocompleteWrap, racePicker, winnerWrap);
    if (placeSelect) row.appendChild(placeSelect);
    if (moveGroup) row.appendChild(moveGroup);
    row.appendChild(removeBtn);

    return row;
}

// ============================================================
// Автокомплит игроков
// ============================================================
function createPlayerAutocomplete(
    draft: GamePlayerDraft,
    onSelect: () => void,
    onPlayerSelected: (player: PlayerWithStats) => void
): { wrapper: HTMLElement; refresh: () => void } {
    const wrapper = document.createElement('div');
    wrapper.className = 'player-autocomplete';

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'player-name-input';
    input.placeholder = t('games.player_placeholder');
    input.autocomplete = 'off';

    const akaEl = document.createElement('span');
    akaEl.className = 'player-autocomplete-aka-hint';
    akaEl.hidden = true;

    const dropdown = document.createElement('div');
    dropdown.className = 'player-autocomplete-dropdown hidden';

    wrapper.append(input, akaEl, dropdown);

    let selectedPlayerId: number | null = draft.player_id;
    let highlightedIndex = -1;
    let matches: PlayerWithStats[] = [];

    function updateAkaBadge(): void {
        const player = cachedPlayers.find((p) => p.id === selectedPlayerId);
        const aka = player?.aka?.trim();
        if (aka) {
            akaEl.textContent = `aka ${aka}`;
            akaEl.hidden = false;
        } else {
            akaEl.textContent = '';
            akaEl.hidden = true;
        }
    }

    function refresh(): void {
        const player = cachedPlayers.find((p) => p.id === selectedPlayerId);
        input.value = player ? player.name : (draft.raw_name ?? '');
        updateAkaBadge();
    }

    function showDropdown(query: string): void {
        const q = query.trim().toLowerCase();
        if (!q) { dropdown.classList.add('hidden'); matches = []; return; }
        matches = cachedPlayers
            .filter((p) => p.name.toLowerCase().includes(q) || (p.aka ?? '').toLowerCase().includes(q))
            .slice(0, 10);
        if (matches.length === 0) {
            dropdown.innerHTML = `<div class="player-autocomplete-empty">${t('games.player_not_found')}</div>`;
            dropdown.classList.remove('hidden');
            return;
        }
        dropdown.innerHTML = '';
        matches.forEach((p, i) => {
            const item = document.createElement('div');
            item.className = 'player-autocomplete-item';
            if (i === highlightedIndex) item.classList.add('is-highlighted');
            const name = document.createElement('span');
            name.className = 'player-autocomplete-name';
            name.textContent = p.name;
            item.appendChild(name);
            if (p.aka) {
                const aka = document.createElement('span');
                aka.className = 'player-autocomplete-aka';
                aka.textContent = ` aka ${p.aka}`;
                item.appendChild(aka);
            }
            item.addEventListener('mousedown', (e) => {
                e.preventDefault();
                selectPlayer(p);
            });
            dropdown.appendChild(item);
        });
        dropdown.classList.remove('hidden');
    }

    function selectPlayer(p: PlayerWithStats): void {
        selectedPlayerId = p.id;
        draft.player_id = p.id;
        draft.raw_name = p.name;
        input.value = p.name;
        dropdown.classList.add('hidden');
        highlightedIndex = -1;
        updateAkaBadge();
        onPlayerSelected(p);
        onSelect();
    }

    input.addEventListener('input', () => {
        const currentPlayer = cachedPlayers.find((p) => p.id === selectedPlayerId);
        if (!currentPlayer || currentPlayer.name !== input.value) {
            selectedPlayerId = null;
            draft.player_id = null;
        }
        draft.raw_name = input.value;
        highlightedIndex = -1;
        updateAkaBadge();
        showDropdown(input.value);
        onSelect();
    });

    input.addEventListener('focus', () => {
        if (input.value.trim()) showDropdown(input.value);
    });

    input.addEventListener('blur', () => {
        setTimeout(() => {
            dropdown.classList.add('hidden');
            if (selectedPlayerId === null) input.value = draft.raw_name ?? '';
        }, 150);
    });

    input.addEventListener('keydown', (e) => {
        if (dropdown.classList.contains('hidden')) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            highlightedIndex = Math.min(highlightedIndex + 1, matches.length - 1);
            showDropdown(input.value);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            highlightedIndex = Math.max(highlightedIndex - 1, -1);
            showDropdown(input.value);
        } else if (e.key === 'Enter') {
            if (highlightedIndex >= 0) {
                e.preventDefault();
                const p = matches[highlightedIndex];
                if (p) selectPlayer(p);
            }
        } else if (e.key === 'Escape') {
            dropdown.classList.add('hidden');
        }
    });

    refresh();
    return { wrapper, refresh };
}

// ============================================================
// Модалки
// ============================================================

// ------------------------------------------------------------
// Импорт игры из реплея SC2
// ------------------------------------------------------------
/** Секунды → «мм:сс» (для подсказки «сколько игрок продержался»). */
function formatReplayTime(totalSeconds: number): string {
    const s = Math.max(0, Math.round(totalSeconds));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Краткая запись общего счёта: 48300 → «48к» (ru) / «48k» (en). */
function formatReplayScore(value: number): string {
    const suffix = getLocale() === 'en' ? 'k' : 'к';
    if (value < 1000) return String(Math.round(value));
    const thousands = value / 1000;
    // До 10к оставляем десятые (5.4к), дальше — целые (48к, 100к).
    return `${thousands.toFixed(thousands < 10 ? 1 : 0).replace(/\.0$/, '')}${suffix}`;
}

/** Полное число с разделителями для тултипа: 48300 → «48 300». */
function formatReplayScoreFull(value: number): string {
    return Math.round(value).toLocaleString(getLocale() === 'en' ? 'en-US' : 'ru-RU');
}

function matchCachedPlayer(name: string | null): PlayerWithStats | undefined {
    return matchReplayPlayer(cachedPlayers, name);
}

/**
 * Ведущего в реплее нет, но наблюдатели в лобби видны. Ищем наблюдателя
 * в справочнике «Проводящих»: сначала точное совпадение ника, затем вхождение
 * (в лобби ник часто с клан-тегом). Не нашли — подсказываем варианты.
 */
function applyReplayHost(observers: string[]): void {
    if (!replayHostHintEl) return;
    replayHostHintEl.classList.add('hidden');
    replayHostHintEl.classList.remove('is-match');
    replayHostHintEl.textContent = '';
    if (!hostSelect || observers.length === 0) return;

    // Ключ сравнения: нижний регистр, без клан-тега, только буквы и цифры.
    const hostKey = (value: string) =>
        normalizeNick(stripClanTag(value)).replace(/[^\p{L}\p{N}]+/gu, '');
    const observerKeys = observers
        .map((name) => hostKey(name))
        .filter((key) => key.length > 0);
    if (observerKeys.length === 0) return;

    const exact = cachedHosts.filter((h) => observerKeys.includes(hostKey(h.name)));
    const matches = exact.length > 0
        ? exact
        : cachedHosts.filter((h) => {
            const key = hostKey(h.name);
            return key.length >= 3
                && observerKeys.some((o) => o.includes(key) || key.includes(o));
        });

    if (matches.length === 1) {
        // Ровно один кандидат — ставим автоматически и помечаем, откуда взялось.
        const match = matches[0]!;
        hostSelect.value = String(match.id);
        replayHostHintEl.textContent = t('games.replay_host_matched', { name: match.name });
        replayHostHintEl.classList.add('is-match');
    } else {
        // Ни одного или несколько — выбор за модератором.
        replayHostHintEl.textContent = t('games.replay_host_candidates', {
            names: observers.join(', '),
        });
    }
    replayHostHintEl.classList.remove('hidden');
}

/**
 * Заполняет открытую форму данными из реплея.
 * Формата в реплее нет; мод подставляется, если сайт уже знает id его настроек лобби; ведущего
 * пытаемся определить по наблюдателям (`applyReplayHost`).
 * Если реплей полный (игра доиграна), места сразу расставляются по условному
 * счёту (экономика + выживание); иначе список только сортируется по нему.
 */
/** Мод по id настроек лобби из реплея; null — такой мод ещё не встречался или совпало несколько. */
function findReplayMod(namespaces: number[] | undefined): GameMod | null {
    if (!namespaces?.length) return null;
    const hits = cachedMods.filter((m) =>
        (m.replay_namespaces ?? []).some((n) => namespaces.includes(Number(n)))
    );
    return hits.length === 1 ? hits[0]! : null;
}

/** То же, что сервер делает при сохранении игры: id настроек переходят к выбранному моду. */
function rememberReplayModLocally(modId: number | null, namespaces: number[]): void {
    if (modId === null || namespaces.length === 0) return;
    cachedMods = cachedMods.map((m) => {
        const current = (m.replay_namespaces ?? []).map(Number);
        const next = m.id === modId
            ? [...new Set([...current, ...namespaces])]
            : current.filter((n) => !namespaces.includes(n));
        return { ...m, replay_namespaces: next };
    });
}

/** Строка с опциями лобби мода под полем «Мод» (только для формы из реплея). */
function renderReplayLobbySettings(): void {
    if (!replayLobbySettingsEl) return;
    const text = formatLobbySettings(currentReplayLobbySettings);
    replayLobbySettingsEl.textContent = text ? `${t('games.lobby_settings')}: ${text}` : '';
    replayLobbySettingsEl.classList.toggle('hidden', !text);
}

async function applyReplayPrefill(prefill: ReplayPrefill): Promise<void> {
    if (playedAtInput) {
        playedAtInput.value = prefill.playedAtMs
            ? toDatetimeLocal(new Date(prefill.playedAtMs))
            : toDatetimeLocal(new Date());
    }
    if (durationInput && prefill.durationSeconds > 0) {
        durationInput.value = String(Math.max(1, Math.round(prefill.durationSeconds / 60)));
    }

    // Карта: сервер ищет по основному и альтернативному названию, но НЕ создаёт
    // новую. Если не нашли — предупреждаем и оставляем выбор вручную.
    if (mapSelect) {
        const prefillMap = (prefill.map as GameMap | null | undefined) ?? findCachedMap(prefill.mapTitle);
        if (prefillMap) {
            if (!cachedMaps.some((m) => m.id === prefillMap.id)) {
                cachedMaps = [...cachedMaps, prefillMap as GameMap]
                    .sort((a, b) => a.name.localeCompare(b.name));
                fillMapSelect();
            }
            setSelectedMap(String(prefillMap.id));
            showMapWarning(null);
        } else if (prefill.mapTitle) {
            setSelectedMap('');
            showMapWarning(prefill.mapTitle);
        }
    }

    // Ведущий: в реплее есть только наблюдатели — по ним ищем «Проводящего».
    applyReplayHost(prefill.observers ?? []);

    // Мод: в реплее есть id его настроек лобби, какой это мод — сайт запомнил по прошлым играм.
    const replayMod = findReplayMod(prefill.modNamespaces);
    if (modSelect && replayMod) modSelect.value = String(replayMod.id);

    // Командный режим — если в реплее есть >= 2 равных команд по >= 2 игрока.
    // id команд в реплее бывают 0-based и/или с пропусками (0,1,2,3), а слоты
    // формы нумеруются с 1: приводим к плотному ряду 1..N, иначе нулевая команда
    // схлопывается с первой и «лишние» игроки теряются в переполненных слотах.
    const rawTeamIds = [...new Set(
        prefill.players
            .map((p) => p.teamId)
            .filter((id): id is number => id !== null)
    )].sort((a, b) => {
        // Команды нумеруем по силе (сумма счёта): сильнейшая получает первые
        // слоты, поэтому места по счёту идут сверху вниз без скачков.
        const sum = (teamId: number) => prefill.players
            .filter((p) => p.teamId === teamId)
            .reduce((acc, p) => acc + (typeof p.score === 'number' ? p.score : 0), 0);
        return sum(b) - sum(a) || a - b;
    });
    const teamIdMap = new Map<number, number>(
        rawTeamIds.map((id, i) => [id, i + 1])
    );
    const teamIds = rawTeamIds.map((id) => teamIdMap.get(id) as number);
    let teamMode = false;
    let teamSize = 2;
    if (teamIds.length >= 2) {
        const sizes = rawTeamIds.map(
            (id) => prefill.players.filter((p) => p.teamId === id).length
        );
        const firstSize = sizes[0] ?? 0;
        if (firstSize >= 2 && sizes.every((s) => s === firstSize)) {
            teamMode = true;
            teamSize = firstSize;
        }
    }
    if (isTeamCheckbox) isTeamCheckbox.checked = teamMode;
    if (teamSizeSelect) teamSizeSelect.value = String(Math.min(4, Math.max(2, teamSize)));
    if (teamCountSelect) teamCountSelect.value = String(Math.max(2, teamIds.length));
    updateTeamModeVisibility();

    // Победитель: явный (result === 'win') либо единственный доживший до конца
    // при условии, что кто-то выбыл (last man standing). Иначе — вручную.
    const survivors = prefill.players.filter(
        (p) => !(typeof p.eliminatedOrder === 'number' && p.eliminatedOrder > 0)
    );
    const eliminatedCount = prefill.players.length - survivors.length;
    const isWin = (p: ReplayPrefillPlayer) => String(p.result ?? '').toLowerCase() === 'win';
    const isLoss = (p: ReplayPrefillPlayer) => String(p.result ?? '').toLowerCase() === 'loss';
    const hasExplicitWin = prefill.players.some(isWin);
    const hasLoss = prefill.players.some(isLoss);

    // В командных и кастомных режимах победителям часто не ставят result=win
    // (остаётся «undecided»), а проигравшим ставят loss. Тогда победитель —
    // единственная команда (или единственный игрок), у которой нет ни одного loss.
    let inferredWinnerNames = new Set<string>();
    if (!hasExplicitWin && hasLoss) {
        if (teamMode) {
            const lostTeams = new Set(
                prefill.players.filter(isLoss).map((p) => p.teamId)
            );
            const aliveTeams = rawTeamIds.filter((id) => !lostTeams.has(id));
            if (aliveTeams.length === 1) {
                inferredWinnerNames = new Set(
                    prefill.players
                        .filter((p) => p.teamId === aliveTeams[0] && p.name)
                        .map((p) => p.name as string)
                );
            }
        } else {
            const notLost = prefill.players.filter((p) => !isLoss(p));
            if (notLost.length === 1 && notLost[0]?.name) {
                inferredWinnerNames = new Set([notLost[0].name]);
            }
        }
    }

    const eligibleSurvivors = (!teamMode && !hasExplicitWin && eliminatedCount >= 1)
        ? survivors.filter((p) => !isLoss(p))
        : [];
    const soleSurvivorName = eligibleSurvivors.length === 1
        ? (eligibleSurvivors[0]?.name ?? null)
        : null;

    const loadedReplay: GamePlayerDraft[] = prefill.players.map((p) => {
        const matched = matchCachedPlayer(p.name);
        const cleanName = p.name ? stripClanTag(p.name) : null;
        const isWinner = isWin(p)
            || (soleSurvivorName !== null && p.name === soleSurvivorName)
            || (p.name !== null && inferredWinnerNames.has(p.name));
        return {
            player_id: matched?.id ?? null,
            race: normalizeReplayRace(p.race),
            team: teamMode && p.teamId !== null ? (teamIdMap.get(p.teamId) ?? null) : null,
            is_winner: isWinner,
            // В реплее нет готовых мест: по умолчанию их проставляем вручную,
            // а для полного реплея ниже расставляем по общему счёту.
            place: null,
            raw_name: cleanName ?? p.name ?? null,
            from_replay: true,
            replay_score: typeof p.score === 'number' ? p.score : null,
            replay_gathered: typeof p.gathered === 'number' ? p.gathered : null,
            replay_killed: typeof p.killed === 'number' ? p.killed : null,
            replay_lost: typeof p.lost === 'number' ? p.lost : null,
            replay_army: typeof p.army === 'number' ? p.army : null,
            replay_survival: typeof p.survivalSeconds === 'number' ? p.survivalSeconds : null,
        };
    });

    // Расставляем игроков по условному счёту из реплея (победитель — сверху),
    // чтобы места было удобно проставлять сверху вниз.
    const orderedReplay = [...loadedReplay].sort((a, b) => {
        if (a.is_winner !== b.is_winner) return a.is_winner ? -1 : 1;
        return (b.replay_score ?? -1) - (a.replay_score ?? -1);
    });

    // Полный реплей — сразу заполняем места 1..N по этому же порядку.
    // Только если счёт есть у всех: иначе места получатся случайными.
    // В командных играх команды уже упорядочены по силе, а места индивидуальные —
    // расставляем их по слотам (сильнейшая команда и её лучшие игроки сверху).
    const allScored = orderedReplay.every((d) => typeof d.replay_score === 'number');
    const placesFromScore = prefill.completeness?.complete === true && allScored;
    let closeScoreGap: number | null = null;

    drafts = teamMode ? buildTeamSlots(orderedReplay) : orderedReplay;

    if (placesFromScore) {
        const scored: GamePlayerDraft[] = [];
        let place = 1;
        for (const d of drafts) {
            if (!d.from_replay) {
                d.place = null; // пустой слот команды места не получает
                continue;
            }
            d.place = place++;
            scored.push(d);
        }
        // Оценка счёта имеет погрешность ~1–2%: если соседние места ближе,
        // порядок мог перевернуться — предупреждаем модератора.
        for (let i = 1; i < scored.length; i++) {
            const prev = scored[i - 1]?.replay_score ?? 0;
            const cur = scored[i]?.replay_score ?? 0;
            if (prev <= 0) continue;
            const gap = (prev - cur) / prev;
            if (closeScoreGap === null || gap < closeScoreGap) closeScoreGap = gap;
        }
        if (closeScoreGap !== null && closeScoreGap >= CLOSE_SCORE_GAP) closeScoreGap = null;
    }

    renderReplayCompleteness(prefill.completeness ?? null, placesFromScore, closeScoreGap);
}

/** Единая точка для кнопки и зоны перетаскивания: проверяем расширение и разбираем файл. */
function openReplayFromFile(file: File): void {
    // При перетаскивании легко зацепить не тот файл — проверяем расширение заранее.
    if (!/\.(sc2replay|replay)$/i.test(file.name)) {
        alert(t('games.replay_not_replay'));
        return;
    }
    void openReplayGameModal(file);
}

async function openReplayGameModal(file: File): Promise<void> {
    if (file.size > 25 * 1024 * 1024) {
        alert(t('games.replay_too_large'));
        return;
    }

    const btn = document.getElementById('btn-add-from-replay') as HTMLButtonElement | null;
    const originalText = btn?.textContent ?? '';
    if (btn) {
        btn.disabled = true;
        btn.textContent = t('games.replay_parsing');
    }
    // Разбор длится несколько секунд — держим зону приёма открытой с надписью «Разбор реплея…».
    const dropZone = document.getElementById('games-replay-dropzone');
    dropZone?.classList.remove('hidden');
    dropZone?.classList.add('is-parsing');

    try {
        const res = await apiUpload<{ replay: ReplayPrefill }>(
            '/api/games/parse-replay',
            file,
            file.name,
            state.token,
            abortController?.signal
        );
        rememberReplayGame(res.replay.replayId, res.replay.duplicate);
        await openCreateGameModal(res.replay);
    } catch (err) {
        alert(t('games.replay_parse_error') + (err instanceof Error ? err.message : String(err)));
    } finally {
        if (dropZone) {
            dropZone.classList.remove('is-parsing');
            dropZone.classList.add('hidden');
        }
        if (btn) {
            btn.disabled = false;
            btn.textContent = originalText;
            applyTranslationsToButton(btn);
        }
    }
}

/** Открывает папку (файлы или сохранённый handle) и разбирает первый батч. */
async function openReplayFolder(source: File[] | ReplayDirHandle): Promise<void> {
    if (replayFolderParsing) return;

    replayFolderParsing = true;
    replayFolderNotice = null;
    updateReplayFolderButtonState();
    renderReplayFolderPanel();
    try {
        const files = Array.isArray(source) ? source : await listReplayFiles(source);
        replayFolderFiles = files;
        replayFolderItems = [];
        replayFolderLoaded = 0;
        replayFolderTotal = files.length;
        replayFolderDone = 0;
        replayFolderNotice = files.length === 0 ? t('games.replays_empty') : null;
        await parseNextReplayBatch();
        void pruneReplayCache();
    } finally {
        replayFolderParsing = false;
        updateReplayFolderButtonState();
        renderReplayFolderPanel();
    }
}

/** Подгружает следующую порцию реплеев из уже выбранной папки. */
async function loadMoreReplays(): Promise<void> {
    if (replayFolderParsing || replayFolderLoaded >= replayFolderFiles.length) return;
    replayFolderParsing = true;
    updateReplayFolderButtonState();
    renderReplayFolderPanel();
    try {
        await parseNextReplayBatch();
    } finally {
        replayFolderParsing = false;
        updateReplayFolderButtonState();
        renderReplayFolderPanel();
    }
}

/**
 * Разбирает следующую порцию файлов. Разбор, который уже есть в кэше,
 * не отправляет файл на сервер повторно.
 */
async function parseNextReplayBatch(): Promise<void> {
    const batch = replayFolderFiles.slice(replayFolderLoaded, replayFolderLoaded + REPLAY_FOLDER_LIMIT);
    for (const file of batch) {
        if (abortController?.signal.aborted) return;

        const cached = await getCachedReplay(file);
        if (cached) {
            replayFolderItems.push({ file, prefill: cached, error: null, cached: true });
        } else {
            try {
                const res = await apiUpload<{ replay: ReplayPrefill }>(
                    '/api/games/parse-replay',
                    file,
                    file.name,
                    state.token,
                    abortController?.signal
                );
                replayFolderItems.push({ file, prefill: res.replay, error: null });
                rememberReplayGame(res.replay.replayId, res.replay.duplicate);
                // В кэш — без пометки о дубликате: она могла устареть.
                void cacheReplay(file, { ...res.replay, duplicate: null });
            } catch (err) {
                if (abortController?.signal.aborted) return;
                replayFolderItems.push({
                    file,
                    prefill: null,
                    error: err instanceof Error ? err.message : String(err),
                });
            }
        }

        replayFolderLoaded += 1;
        replayFolderDone = replayFolderItems.length;
        renderReplayFolderPanel();
    }

    // Реплеи из кэша не ходят на сервер — дубликаты проверяем одним запросом на порцию.
    const ids = replayFolderItems
        .map((item) => item.prefill?.replayId ?? '')
        .filter(Boolean);
    const before = replayGameHits.size;
    await checkReplayDuplicates(ids);
    if (replayGameHits.size !== before) renderReplayFolderPanel();
}

/** Фильтрует и сортирует файлы, полученные через обычный input папки. */
function replayFileList(files: File[]): File[] {
    return files
        .filter((f) => /\.(sc2replay|replay)$/i.test(f.name))
        .sort((a, b) => b.lastModified - a.lastModified);
}

/** Кнопка «Папка реплеев»: сохранённая папка или выбор новой. */
async function chooseReplayFolder(): Promise<void> {
    if (replayFolderParsing) return;

    await ensureRolesLoaded();
    if (!canImportReplays()) {
        alert(t('games.match_add_forbidden'));
        return;
    }

    if (!isDirectoryPickerSupported()) {
        // Fallback: обычный выбор папки (не сохраняется между перезагрузками).
        document.getElementById('games-replay-folder')?.click();
        return;
    }

    const saved = replayFolderHandle ?? replayFolderRemembered;
    if (saved) {
        let permission = await queryReplayDirectoryPermission(saved);
        if (permission !== 'granted') permission = await requestReplayDirectoryPermission(saved);
        if (permission === 'granted') {
            replayFolderHandle = saved;
            replayFolderRemembered = null;
            void rememberReplayDirectory(saved);
            await openReplayFolder(saved);
            return;
        }
    }

    await pickAndOpenReplayFolder();
}

/** Всегда открывает выбор новой папки (кнопка «Сменить папку»). */
async function pickAndOpenReplayFolder(): Promise<void> {
    if (replayFolderParsing) return;
    try {
        const handle = await pickReplayDirectory();
        if (!handle) return;
        replayFolderHandle = handle;
        replayFolderRemembered = null;
        await rememberReplayDirectory(handle);
        await openReplayFolder(handle);
    } catch (err) {
        console.error('[games] replay folder pick error:', err);
    }
}

/** При открытии страницы подхватывает сохранённую папку, если доступ уже есть. */
async function restoreReplayFolder(): Promise<void> {
    await ensureRolesLoaded();
    if (!canImportReplays() || !isDirectoryPickerSupported()) return;

    const saved = await getRememberedReplayDirectory();
    if (!saved) return;

    const permission = await queryReplayDirectoryPermission(saved);
    if (permission === 'granted') {
        replayFolderHandle = saved;
        replayFolderRemembered = null;
        await openReplayFolder(saved);
    } else {
        replayFolderRemembered = saved;
        replayFolderNotice = t('games.replays_restore_hint');
        renderReplayFolderPanel();
    }
}

/** Сбрасывает выбранную/сохранённую папку и очищает список. */
async function resetReplayFolder(): Promise<void> {
    if (replayFolderParsing) return;
    replayFolderHandle = null;
    replayFolderRemembered = null;
    replayFolderFiles = [];
    replayFolderItems = [];
    replayFolderLoaded = 0;
    replayFolderTotal = 0;
    replayFolderDone = 0;
    replayFolderNotice = null;
    updateReplayFolderButtonState();
    renderReplayFolderPanel();
    await forgetReplayDirectory();
}

/** Кнопка на время разбора — неактивна и с подсказкой. */
function updateReplayFolderButtonState(): void {
    const btn = document.getElementById('btn-games-replays') as HTMLButtonElement | null;
    if (!btn) return;
    btn.disabled = replayFolderParsing;
    btn.textContent = replayFolderParsing ? t('games.replay_parsing') : t('games.replays_button');
}

/** Права на импорт игр из реплея (как у кнопок добавления игры). */
function canImportReplays(): boolean {
    return Boolean(state.user?.is_moderator || state.user?.is_admin);
}

/** Дата/время игры для пометок о дубликатах. */
function formatReplayGameDate(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(getLocale(), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Запрашивает у сервера, какие реплеи уже добавлены играми. Проверяем один раз
 * на id реплея; результат живёт до перезагрузки страницы.
 */
async function checkReplayDuplicates(ids: string[]): Promise<void> {
    const pending = [...new Set(ids)].filter(
        (id) => id && !replayCheckedIds.has(id) && !replayGameHits.has(id)
    );
    if (pending.length === 0) return;
    try {
        const res = await apiRequest<{ games: Record<string, { game_id: number; played_at: string | null }> }>(
            '/api/games/replay-check',
            { method: 'POST', token: state.token, body: { replay_ids: pending } }
        );
        for (const id of pending) {
            const hit = res.games?.[id];
            if (hit) replayGameHits.set(id, hit);
            else replayCheckedIds.add(id);
        }
    } catch (err) {
        // Проверка не критична: без неё просто не будет пометки.
        console.warn('[games] replay duplicate check failed:', err);
    }
}

/** Запоминает, что из реплея уже есть игра (после разбора или сохранения). */
function rememberReplayGame(replayId: string | null | undefined, hit: { game_id: number; played_at: string | null } | null | undefined): void {
    if (!replayId) return;
    if (hit) replayGameHits.set(replayId, hit);
    else replayCheckedIds.add(replayId);
}

/** Плашка в форме игры: «реплей уже был добавлен» + ссылка на эту игру в новой вкладке. */
function renderReplayDuplicate(dup: { game_id: number; played_at: string | null } | null | undefined): void {
    if (!replayDuplicateEl) return;
    replayDuplicateEl.innerHTML = '';
    if (!dup) {
        replayDuplicateEl.classList.add('hidden');
        return;
    }
    const date = formatReplayGameDate(dup.played_at);
    const text = document.createElement('span');
    text.textContent = date
        ? t('games.replay_duplicate', { date })
        : t('games.replay_duplicate_no_date');

    const link = document.createElement('a');
    link.className = 'replay-duplicate-link';
    link.href = `/games?game=${dup.game_id}`;
    link.target = '_blank';
    link.rel = 'noopener';
    link.textContent = t('games.replay_duplicate_open');

    replayDuplicateEl.append(text, link);
    replayDuplicateEl.classList.remove('hidden');
}

/** Перерисовывает панель последних реплеев. */
function renderReplayFolderPanel(): void {
    if (!replayFolderPanelEl) return;

    const visible = replayFolderParsing || replayFolderItems.length > 0 || replayFolderNotice !== null;
    replayFolderPanelEl.classList.toggle('hidden', !visible);
    if (!visible) {
        replayFolderPanelEl.innerHTML = '';
        return;
    }

    replayFolderPanelEl.innerHTML = '';

    const head = document.createElement('div');
    head.className = 'games-replays-head';

    const title = document.createElement('span');
    title.className = 'games-replays-title';
    title.textContent = `🎬 ${t('games.replays_title')}`;
    head.appendChild(title);

    const status = document.createElement('span');
    status.className = 'games-replays-status';
    if (replayFolderParsing) {
        status.textContent = t('games.replays_parsing', {
            done: replayFolderDone,
            total: replayFolderTotal,
        });
    } else if (replayFolderTotal > 0) {
        status.textContent = t('games.replays_done', {
            n: replayFolderLoaded,
            total: replayFolderTotal,
        });
    }
    head.appendChild(status);

    const hasFolder = Boolean(
        replayFolderHandle
        || replayFolderRemembered
        || replayFolderFiles.length > 0
        || replayFolderItems.length > 0
    );

    if (isDirectoryPickerSupported()) {
        const change = document.createElement('button');
        change.type = 'button';
        change.className = 'games-replays-action';
        change.disabled = replayFolderParsing;
        change.textContent = t('games.replays_change');
        change.addEventListener('click', () => void pickAndOpenReplayFolder());
        head.appendChild(change);
    }

    if (hasFolder) {
        const reset = document.createElement('button');
        reset.type = 'button';
        reset.className = 'games-replays-action games-replays-action--danger';
        reset.disabled = replayFolderParsing;
        reset.textContent = t('games.replays_reset');
        reset.addEventListener('click', () => void resetReplayFolder());
        head.appendChild(reset);
    }

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'games-replays-close';
    close.title = t('games.replays_close');
    close.textContent = '✕';
    close.addEventListener('click', () => {
        if (replayFolderParsing) return;
        replayFolderItems = [];
        replayFolderNotice = null;
        replayFolderTotal = 0;
        replayFolderDone = 0;
        renderReplayFolderPanel();
    });
    head.appendChild(close);

    replayFolderPanelEl.appendChild(head);

    if (!replayFolderParsing && replayFolderItems.length > 0) {
        const hint = document.createElement('p');
        hint.className = 'games-replays-hint';
        hint.textContent = t('games.replays_hint');
        replayFolderPanelEl.appendChild(hint);
    }

    const list = document.createElement('div');
    list.className = 'games-replays-list';

    if (replayFolderNotice) {
        const p = document.createElement('p');
        p.className = 'games-replays-empty';
        p.textContent = replayFolderNotice;
        list.appendChild(p);
    }

    for (const item of replayFolderItems) list.appendChild(buildReplayFolderRow(item));
    replayFolderPanelEl.appendChild(list);

    if (!replayFolderParsing && replayFolderLoaded < replayFolderFiles.length) {
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'games-replays-more';
        more.textContent = t('games.replays_more');
        more.addEventListener('click', () => void loadMoreReplays());
        replayFolderPanelEl.appendChild(more);
    }
}

/** Строка одного реплея: клик сразу открывает заполненную форму новой игры. */
function buildReplayFolderRow(item: ReplayFolderItem): HTMLElement {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'games-replay';

    const main = document.createElement('span');
    main.className = 'games-replay-main';

    const mapName = document.createElement('span');
    mapName.className = 'games-replay-map';
    mapName.textContent = item.prefill?.map?.name ?? item.prefill?.mapTitle ?? item.file.name;
    main.appendChild(mapName);

    // Реплей уже добавляли игрой — затемняем строку и помечаем датой той игры.
    const dup = item.prefill ? replayGameHits.get(item.prefill.replayId) : null;
    let dupText = '';
    if (dup) {
        row.classList.add('games-replay--duplicate');
        const date = formatReplayGameDate(dup.played_at);
        dupText = date ? t('games.replays_duplicate', { date }) : t('games.replays_duplicate_no_date');
        const badge = document.createElement('span');
        badge.className = 'games-replay-dupe';
        badge.textContent = dupText;
        main.appendChild(badge);
    }

    const meta = document.createElement('span');
    meta.className = 'games-replay-meta';

    if (item.error) {
        row.classList.add('games-replay--error');
        row.disabled = true;
        meta.textContent = t('games.replay_parse_error') + item.error;
    } else if (!item.prefill) {
        row.disabled = true;
        row.classList.add('is-pending');
        meta.textContent = t('games.replay_parsing');
    } else {
        const p = item.prefill;
        const parts: string[] = [];
        if (p.playedAt) {
            const d = new Date(p.playedAt);
            if (!Number.isNaN(d.getTime())) {
                parts.push(d.toLocaleString(getLocale(), {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                }));
            }
        }
        parts.push(t('games.replays_duration', { n: Math.max(1, Math.round(p.durationSeconds / 60)) }));
        parts.push(t('games.replays_players', { n: p.players.length }));
        if (item.cached) parts.push(t('games.replays_cached'));
        meta.textContent = parts.join(' · ');

        if (!p.map) {
            const warn = document.createElement('span');
            warn.className = 'games-replay-missing';
            warn.textContent = t('games.replays_map_missing');
            main.appendChild(warn);
        }
    }
    main.appendChild(meta);

    // Полный текст в подсказке: в узкой строке он обрезается многоточием.
    row.title = [mapName.textContent, dupText, meta.textContent].filter(Boolean).join(' · ');

    const players = document.createElement('span');
    players.className = 'games-replay-players';
    players.textContent = (item.prefill?.players ?? [])
        .map((p) => p.name)
        .filter((n): n is string => Boolean(n))
        .join(', ');

    row.append(main, players);

    if (item.prefill && !item.error) {
        const action = document.createElement('span');
        action.className = 'games-replay-add';
        action.textContent = `${t('games.replays_add')} →`;
        row.appendChild(action);

        row.addEventListener('click', () => {
            if (!item.prefill) return;
            void openCreateGameModal(item.prefill);
        });
    }

    return row;
}

/** Возвращает кнопке перевод после временного текста «Разбор реплея…». */
function applyTranslationsToButton(btn: HTMLButtonElement): void {
    if (btn.dataset.i18n === 'games.from_replay') btn.textContent = t('games.from_replay');
}

/**
 * Плашка о полноте реплея: «полный» — игра доиграна, иначе перечисляем причины.
 * Это подсказка для модератора, сохранение она не блокирует.
 */
function renderReplayCompleteness(
    info: ReplayPrefill['completeness'],
    placesAutofilled = false,
    closeScoreGap: number | null = null
): void {
    if (!replayCompletenessEl) return;

    replayCompletenessEl.classList.add('hidden');
    replayCompletenessEl.classList.remove('is-complete', 'is-incomplete', 'is-uncertain');
    replayCompletenessEl.textContent = '';
    if (!info) return;

    if (info.complete) {
        replayCompletenessEl.classList.add('is-complete');
        const parts = [
            t(placesAutofilled ? 'games.replay_complete_places' : 'games.replay_complete'),
        ];
        if (closeScoreGap !== null) {
            // Счёт соседей почти равен — места по оценке могут быть перепутаны.
            replayCompletenessEl.classList.add('is-uncertain');
            parts.push(
                t('games.replay_score_close', {
                    gap: `${Math.max(1, Math.round(closeScoreGap * 100))}%`,
                })
            );
        }
        replayCompletenessEl.textContent = parts.join(' ');
    } else {
        const parts: string[] = [];
        if (info.reasons.includes('no_winner')) {
            parts.push(t('games.replay_incomplete_no_winner'));
        }
        if (info.reasons.includes('truncated_data')) {
            parts.push(t('games.replay_incomplete_truncated', {
                gap: formatReplayTime(info.endGapSeconds ?? 0),
            }));
        }
        replayCompletenessEl.classList.add('is-incomplete');
        replayCompletenessEl.textContent = parts.join(' ');
    }
    replayCompletenessEl.classList.remove('hidden');
}

/** Забирает (и удаляет) черновик от «Менеджера матча» из sessionStorage. */
function takeMatchPrefill(): MatchGamePrefill | null {
    try {
        const raw = sessionStorage.getItem(MATCH_PREFILL_KEY);
        sessionStorage.removeItem(MATCH_PREFILL_KEY);
        if (!raw) return null;
        return JSON.parse(raw) as MatchGamePrefill;
    } catch {
        return null;
    }
}

async function openCreateGameModal(
    prefill?: ReplayPrefill,
    fromGame?: GameFull,
    matchPrefill?: MatchGamePrefill
): Promise<void> {
    if (!modal) return;
    editingGameId = null;
    isDirty = false;
    // Id реплея запоминаем: при сохранении он уйдёт в игру, чтобы ловить дубликаты.
    currentReplayId = prefill?.replayId ?? null;
    currentReplayModNamespaces = prefill?.modNamespaces ?? [];
    currentReplayLobbySettings = prefill?.lobbySettings ?? [];
    currentReplayArcadeMap = prefill?.arcadeMap ?? null;
    renderReplayLobbySettings();
    if (modalTitle) modalTitle.textContent = t('games.form_title');
    // Плашка о полноте реплея — только для импорта из файла.
    renderReplayCompleteness(null);
    renderReplayDuplicate(
        prefill ? (replayGameHits.get(prefill.replayId) ?? prefill.duplicate ?? null) : null
    );
    applyReplayHost([]);

    await Promise.all([refreshPlayersCache(), ensureAllRefsLoaded()]);

    hideMapDropdown();

    fillSelect(formatSelect, cachedFormats, t('games.select_placeholder'));
    fillSelect(hostSelect, cachedHosts, t('games.select_placeholder'));
    fillMapSelect();
    fillSelect(modSelect, cachedMods, t('games.select_placeholder'), t('games.no_mod'));

    const useLastCheckbox = document.getElementById('field-use-last') as HTMLInputElement | null;
    const useLast = !prefill && !fromGame && !matchPrefill && (useLastCheckbox?.checked ?? true);
    const lastGame = useLast ? loadLastGameSettings() : null;

    if (lastGame) {
        if (formatSelect && lastGame.format_id && cachedFormats.some((f) => f.id === lastGame.format_id)) {
            formatSelect.value = String(lastGame.format_id);
        }
        if (hostSelect && lastGame.host_id && cachedHosts.some((h) => h.id === lastGame.host_id)) {
            hostSelect.value = String(lastGame.host_id);
        }
        if (lastGame.map_id && activeMaps().some((m) => m.id === lastGame.map_id)) {
            setSelectedMap(String(lastGame.map_id));
        }
        if (modSelect && lastGame.mod_id && cachedMods.some((m) => m.id === lastGame.mod_id)) {
            modSelect.value = String(lastGame.mod_id);
        }
        if (durationInput) durationInput.value = lastGame.duration_min ? String(lastGame.duration_min) : '';
        if (isTeamCheckbox) isTeamCheckbox.checked = lastGame.is_team;
        if (teamSizeSelect) teamSizeSelect.value = String(lastGame.team_size ?? 2);
        if (teamCountSelect) teamCountSelect.value = String(Math.max(2, lastGame.team_count ?? 2));
        updateTeamModeVisibility();

        if (playedAtInput) {
            if (lastGame.played_at) {
                playedAtInput.value = lastGame.played_at;
            } else {
                playedAtInput.value = toDatetimeLocal(new Date());
            }
        }

        const loadedLast = lastGame.players.map((p) => ({
            player_id: p.player_id,
            race: p.race,
            team: p.team,
            is_winner: false,
            place: null,
        }));
        drafts = lastGame.is_team ? buildTeamSlots(loadedLast) : loadedLast;
    } else {
        if (playedAtInput) playedAtInput.value = toDatetimeLocal(new Date());
        if (durationInput) durationInput.value = '';
        if (isTeamCheckbox) isTeamCheckbox.checked = false;
        if (teamSizeSelect) teamSizeSelect.value = '2';
        if (teamCountSelect) teamCountSelect.value = '2';
        updateTeamModeVisibility();
        if (formatSelect) {
            const fanFfa = cachedFormats.find((f) => f.slug === 'fan-ffa');
            if (fanFfa) formatSelect.value = String(fanFfa.id);
        }
        drafts = [];
        for (let i = 0; i < 4; i++) {
            drafts.push({ player_id: null, race: 'T', team: null, is_winner: false, place: null });
        }
    }

    if (notesInput) notesInput.value = '';

    if (matchPrefill) {
        applyMatchPrefill(matchPrefill);
        // Опции лобби из реплея, импортированного в «Менеджере матча».
        currentReplayLobbySettings = matchPrefill.lobby_settings ?? [];
        renderReplayLobbySettings();
        showMapWarning(null);
        updateMapEditButton();
    } else if (prefill) {
        await applyReplayPrefill(prefill);
    } else {
        if (fromGame) applyGameSettingsFromGame(fromGame);
        showMapWarning(null);
        updateMapEditButton();
    }

    normalizePlaces();
    renderPlayerDrafts();
    modal.classList.remove('hidden');
}

/**
 * Переносит настройки существующей игры в форму новой:
 * формат/ведущий/карту/мод/длительность и состав участников (раса, команда).
 * Победитель и места НЕ переносятся — их нужно проставить заново.
 */
function applyGameSettingsFromGame(gameFull: GameFull): void {
    if (playedAtInput && gameFull.played_at) {
        playedAtInput.value = toDatetimeLocal(new Date(gameFull.played_at));
    }
    if (formatSelect && gameFull.format && cachedFormats.some((f) => f.id === gameFull.format!.id)) {
        formatSelect.value = String(gameFull.format.id);
    }
    if (hostSelect && gameFull.host && cachedHosts.some((h) => h.id === gameFull.host!.id)) {
        hostSelect.value = String(gameFull.host.id);
    }
    if (gameFull.map && activeMaps().some((m) => m.id === gameFull.map!.id)) {
        setSelectedMap(String(gameFull.map.id));
    }
    if (modSelect && gameFull.mod && cachedMods.some((m) => m.id === gameFull.mod!.id)) {
        modSelect.value = String(gameFull.mod.id);
    } else if (modSelect) {
        modSelect.value = '';
    }

    if (durationInput) {
        durationInput.value = gameFull.duration_min ? String(gameFull.duration_min) : '';
    }

    const hasTeams = gameFull.players.some((p) => p.team !== null);
    if (isTeamCheckbox) isTeamCheckbox.checked = hasTeams;
    if (hasTeams && teamSizeSelect) {
        const firstTeam = gameFull.players[0]?.team ?? 1;
        const size = gameFull.players.filter((p) => p.team === firstTeam).length;
        teamSizeSelect.value = String(size);
        const teamCount = Math.max(...gameFull.players.map((p) => p.team ?? 1));
        if (teamCountSelect) teamCountSelect.value = String(Math.max(2, teamCount));
    }
    updateTeamModeVisibility();

    const loadedFromGame = gameFull.players.map((p) => ({
        player_id: p.player_id,
        race: p.race,
        team: p.team,
        is_winner: false,
        place: null,
    }));
    drafts = hasTeams ? buildTeamSlots(loadedFromGame) : loadedFromGame;
}

/**
 * Переносит черновик «Менеджера матча» в форму новой игры:
 * дата/время, карта, мод и участники (раса). Формат/ведущего подбирает
 * пользователь; победитель и места проставляются вручную.
 */
function applyMatchPrefill(p: MatchGamePrefill): void {
    if (playedAtInput) playedAtInput.value = p.played_at || toDatetimeLocal(new Date());
    if (p.map_id && activeMaps().some((m) => m.id === p.map_id)) {
        setSelectedMap(String(p.map_id));
    } else {
        setSelectedMap('');
    }
    if (modSelect) {
        modSelect.value = p.mod_id && cachedMods.some((m) => m.id === p.mod_id) ? String(p.mod_id) : '';
    }
    if (isTeamCheckbox) isTeamCheckbox.checked = false;
    updateTeamModeVisibility();

    drafts = p.players.map((pl) => ({
        player_id: pl.player_id,
        race: pl.race,
        team: null,
        is_winner: false,
        place: null,
    }));
}

/** Открывает форму новой игры с настройками указанной игры. */
async function openCreateFromGame(gameId: number): Promise<void> {
    try {
        const res = await apiRequest<{ game: GameFull }>(`/api/games/${gameId}`);
        await openCreateGameModal(undefined, res.game);
    } catch (err) {
        alert(t('games.load_one_error') + (err instanceof Error ? err.message : String(err)));
    }
}

async function openEditGameModal(gameId: number): Promise<void> {
    if (!modal) return;

    // Редактирование не трогает replay_id игры — id реплея не отправляем.
    currentReplayId = null;
    currentReplayModNamespaces = [];
    currentReplayLobbySettings = [];
    currentReplayArcadeMap = null;
    renderReplayLobbySettings();
    renderReplayDuplicate(null);

    let gameFull: GameFull;
    try {
        const res = await apiRequest<{ game: GameFull }>(`/api/games/${gameId}`);
        gameFull = res.game;
    } catch (err) {
        alert(t('games.load_one_error') + (err instanceof Error ? err.message : String(err)));
        return;
    }

    await Promise.all([refreshPlayersCache(), ensureAllRefsLoaded()]);

    if (modalTitle) modalTitle.textContent = t('games.form_title_edit');
    hideMapDropdown();

    fillSelect(formatSelect, cachedFormats, t('games.select_placeholder'));
    fillSelect(hostSelect, cachedHosts, t('games.select_placeholder'));
    fillMapSelect();
    fillSelect(modSelect, cachedMods, t('games.select_placeholder'), t('games.no_mod'));

    if (formatSelect && gameFull.format) formatSelect.value = String(gameFull.format.id);
    if (hostSelect && gameFull.host) hostSelect.value = String(gameFull.host.id);
    if (gameFull.map) {
        const selectable = activeMaps().some((m) => m.id === gameFull.map!.id);
        if (selectable) {
            setSelectedMap(String(gameFull.map.id));
        } else {
            setSelectedMap('');
            showMapWarning(gameFull.map.name, true);
        }
    }
    if (modSelect && gameFull.mod) modSelect.value = String(gameFull.mod.id);
    else if (modSelect) modSelect.value = '';

    if (playedAtInput) playedAtInput.value = toDatetimeLocal(new Date(gameFull.played_at));
    if (durationInput) durationInput.value = gameFull.duration_min ? String(gameFull.duration_min) : '';
    if (notesInput) notesInput.value = gameFull.notes ?? '';

    // Определяем командный режим
    const hasTeams = gameFull.players.some((p) => p.team !== null);
    if (isTeamCheckbox) isTeamCheckbox.checked = hasTeams;
    if (hasTeams && teamSizeSelect) {
        const firstTeam = gameFull.players[0]?.team ?? 1;
        const size = gameFull.players.filter((p) => p.team === firstTeam).length;
        teamSizeSelect.value = String(size);
        const teamCount = Math.max(...gameFull.players.map((p) => p.team ?? 1));
        if (teamCountSelect) teamCountSelect.value = String(Math.max(2, teamCount));
    }
    updateTeamModeVisibility();

    // Преобразуем сохранённый порядок выбывания в индивидуальные места.
    const totalPlayers = gameFull.players.length;

    const loadedEdit = gameFull.players.map((p) => ({
        player_id: p.player_id,
        race: p.race,
        team: p.team,
        is_winner: p.is_winner,
        place: entryPlace(p, totalPlayers),
    }));
    drafts = hasTeams ? buildTeamSlots(loadedEdit) : loadedEdit;

    normalizePlaces();
    renderPlayerDrafts();
    editingGameId = gameId;
    isDirty = false;
    modal.classList.remove('hidden');
}

function closeGameModal(force = false): void {
    if (!force && !confirmClose()) return;
    modal?.classList.add('hidden');
    form?.reset();
    drafts = [];
    editingGameId = null;
    isDirty = false;
}

// ============================================================
// Отправка формы
// ============================================================
async function submitGameForm(e: Event): Promise<void> {
    e.preventDefault();
    if (!form) return;

    if (!playedAtInput?.value) { alert(t('games.error_no_date')); return; }
    if (!formatSelect || !hostSelect || !mapSelect || !modSelect) return;
    if (!formatSelect.value || !hostSelect.value || !mapSelect.value) {
        alert(t('games.error_no_refs')); return;
    }

    const unresolved = drafts.filter((d) => d.from_replay && !d.player_id && d.raw_name);
    if (unresolved.length > 0) {
        alert(t('games.replay_unresolved', { names: unresolved.map((d) => d.raw_name).join(', ') }));
        return;
    }

    // Слот с введённым ником, но без сопоставленного player_id (ник напечатан или
    // подставлен автозаполнением без выбора из списка) молча выпал бы из состава и
    // сломал проверку мест — поэтому требуем явно выбрать игрока.
    const unselected = drafts.filter((d) => !d.player_id && (d.raw_name ?? '').trim() !== '');
    if (unselected.length > 0) {
        alert(t('games.error_player_not_selected', { names: unselected.map((d) => d.raw_name).join(', ') }));
        return;
    }

    const filledPlayers = drafts.filter((d) => d.player_id !== null);
    if (filledPlayers.length === 0) { alert(t('games.error_no_players')); return; }

    const ids = filledPlayers.map((d) => d.player_id!);
    if (new Set(ids).size !== ids.length) { alert(t('games.error_duplicate_player')); return; }

    const hasWinner = filledPlayers.some((d) => d.is_winner);
    if (!hasWinner) { alert(t('games.error_no_winner')); return; }

    const teamMode = isTeamMode();

    if (teamMode) {
        const teamSize = getTeamSize();
        const teamCount = getTeamCount();

        // Все слоты должны быть заполнены (пустые недопустимы).
        if (filledPlayers.length !== teamCount * teamSize) {
            alert(t('games.error_teams_incomplete', { teams: teamCount, size: teamSize })); return;
        }

        const teamsMap = new Map<number, typeof filledPlayers>();
        for (const p of filledPlayers) {
            const tNum = p.team ?? 0;
            const arr = teamsMap.get(tNum) ?? [];
            arr.push(p);
            teamsMap.set(tNum, arr);
        }
        if (teamsMap.size < 2) { alert(t('games.error_need_two_teams')); return; }
        const sizes = [...teamsMap.values()].map((arr) => arr.length);
        if (new Set(sizes).size !== 1 || sizes[0] !== teamSize) {
            alert(t('games.error_teams_uneven', { size: teamSize })); return;
        }

        const winnerTeams: number[] = [];
        for (const [tNum, players] of teamsMap) {
            if (players.every((p) => p.is_winner)) winnerTeams.push(tNum);
            else if (players.some((p) => p.is_winner)) {
                alert(t('games.error_incomplete_winner_team')); return;
            }
        }
        if (winnerTeams.length !== 1) { alert(t('games.error_multiple_winner_teams')); return; }

        // Места индивидуальны для каждого игрока: уникальные 1..N.
        if (filledPlayers.some((d) => d.place === null || d.place < 1 || d.place > filledPlayers.length)) {
            alert(t('games.error_missing_places')); return;
        }
        const places = filledPlayers.map((d) => d.place!);
        if (new Set(places).size !== places.length) {
            alert(t('games.error_duplicate_place')); return;
        }
    } else {
        // Места: у всех, кроме победителя, должны быть уникальные места 2..N.
        const nonWinners = filledPlayers.filter((d) => !d.is_winner);
        if (nonWinners.some((d) => d.place === null || d.place <= 1 || d.place > filledPlayers.length)) {
            alert(t('games.error_missing_places')); return;
        }
        const places = nonWinners.map((d) => d.place!);
        if (new Set(places).size !== places.length) {
            alert(t('games.error_duplicate_place')); return;
        }
    }

    // Место хранится в БД как порядок выбывания (обратное преобразование).
    // Масштаб — число игроков, даже в командных играх места индивидуальны.
    const scale = filledPlayers.length;

    const playersPayload = filledPlayers.map((d) => ({
        player_id: d.player_id,
        race: d.race,
        team: d.team,
        is_winner: d.is_winner,
        eliminated_at: placeToElim(d.place, scale),
    }));

    const playedAtDate = new Date(playedAtInput.value);
    const payload = {
        played_at: playedAtDate.toISOString(),
        format_id: Number(formatSelect.value),
        host_id: Number(hostSelect.value),
        map_id: Number(mapSelect.value),
        mod_id: modSelect.value ? Number(modSelect.value) : null,
        duration_min: durationInput?.value ? Number(durationInput.value) : null,
        notes: notesInput?.value?.trim() || null,
        track_elim: true,
        players: playersPayload,
        // Игра создана из реплея — запоминаем его id (для проверки дубликатов).
        ...(currentReplayId ? { replay_id: currentReplayId } : {}),
        ...(currentReplayModNamespaces.length > 0 ? { replay_mod_namespaces: currentReplayModNamespaces } : {}),
        ...(currentReplayLobbySettings.length > 0 ? { replay_lobby_settings: currentReplayLobbySettings } : {}),
    };

    const saveBtn = document.getElementById('btn-save-game') as HTMLButtonElement | null;
    if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = t('common.saving'); }

    try {
        const editingId = editingGameId;
        if (editingId !== null) {
            const res = await apiRequest<{ game: GameFull }>(`/api/games/${editingId}`, {
                method: 'PATCH',
                token: state.token,
                body: payload,
            });
            saveLastGameSettings();
            closeGameModal(true);
            // Обновляем только карточку игры, без полной перезагрузки списка.
            if (res.game) applyEditedGame(res.game);
            else await loadGames();
        } else {
            const created = await apiRequest<{ game?: GameFull; game_id?: number }>(
                '/api/games',
                { method: 'POST', token: state.token, body: payload }
            );
            // Реплей только что добавили — в папке он больше не «новый».
            const newId = created?.game?.id ?? created?.game_id;
            if (currentReplayId && newId) {
                rememberReplayGame(currentReplayId, {
                    game_id: Number(newId),
                    played_at: payload.played_at,
                });
                renderReplayFolderPanel();
            }
            rememberReplayModLocally(payload.mod_id, currentReplayModNamespaces);
            saveLastGameSettings();
            closeGameModal(true);
            await loadGames();
        }
    } catch (err) {
        alert(t('games.save_error') + (err instanceof Error ? err.message : String(err)));
    } finally {
        if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = t('common.save'); }
    }
}

// ============================================================
// Удаление игры
// ============================================================
async function deleteGame(g: GameListItem): Promise<void> {
    const confirmed = confirm(
        t('games.delete_confirm', {
            date: new Date(g.played_at).toLocaleDateString(),
            winners: g.winners.join(', '),
        })
    );
    if (!confirmed) return;
    try {
        await apiRequest(`/api/games/${g.id}`, { method: 'DELETE', token: state.token });
        removeGameFromList(g.id);
    } catch (err) {
        alert(t('games.delete_error') + (err instanceof Error ? err.message : String(err)));
    }
}